import { medicalAidDisplay } from 'src/app/core/utils/scheme-code';
import { patientGroupId } from 'src/app/core/utils/patient-group';
import { UsageService } from '../../../../core/usage/usage.service';
import { PatientAppShareDialogComponent, PatientAppShareDialogData } from '../patient-app-share-dialog/patient-app-share-dialog.component';
import { PatientAppointmentService } from './../../../../core/services/patient-appointment.service';
import { TaskDialogComponent, TaskDialogData, TaskDialogResult } from '../../../../shared/components/task-dialog/task-dialog.component';
import { ageFromDob, sexFromPatient, toOnsetDate } from 'src/app/shared/util/icd-coding-context.util';
import { controlTarget, FindReplaceTarget, targets } from 'src/app/shared/util/find-replace-scope';
import { ThemeService } from './../../../../core/services/theme.service';
import { SpeechRecognitionService } from 'src/app/core/services/speech-recognition.service';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PatientLabService } from './../../../../core/services/patient-lab.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectionList } from '@angular/material/list';
import { MatSelectChange } from '@angular/material/select';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { EMPTY, Observable, Subscription, combineLatest, forkJoin, map, of, startWith, switchMap, takeUntil } from 'rxjs';
import { IcdCodeSelectorComponent } from '../icd-code-selector/icd-code-selector.component';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { checkIfGroupIsMedical, checkIfGroupIsSurgical, SPECIALITIES, TITLES } from './../../../../core/data/app.data';
import { FileObject } from './../../../../core/interfaces/file-object';
import { AnalyticsService } from './../../../../core/services/analytics.service';
import { IcdCode, ProcedureCode } from './../../../../core/services/app-data.service';
import { CodingBundleService } from './../../../../core/services/coding-bundle.service';
import { ColleagueService } from './../../../../core/services/colleague.service';
import { ConsentFormsService } from './../../../../core/services/consent-forms.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { isStateModeGroup } from '../../../../shared/util/state-mode.util';
import { NoteTypeService } from './../../../../core/services/note-type.service';
import { PatientAdmissionHistoryService } from './../../../../core/services/patient-admission-history.service';
import { PatientAiService } from './../../../../core/services/patient-ai.service';
import { PatientCommunicationService } from './../../../../core/services/patient-communication.service';
import { PatientConsentService } from './../../../../core/services/patient-consent.service';
import { PatientInviteService } from './../../../../core/services/patient-invite.service';
import { PatientNoteService } from './../../../../core/services/patient-note.service';
import { PatientTaskService } from './../../../../core/services/patient-task.service';
import { CaptureResponse, CompareResponse, PlannedProcedureCodeService } from './../../../../core/services/planned-procedure-code.service';
import { TheatreTeamPrefillResponse, TheatreTeamService } from './../../../../core/services/theatre-team.service';
import { CodePromptingService } from './../../../../core/services/code-prompting.service';
import { DisciplineConfigService } from './../../../../core/services/discipline-config.service';
import { PatientService } from './../../../../core/services/patient.service';
import { PdfDownloadService } from './../../../../core/services/pdf-download.service';
import { ScrollPositionService } from './../../../../core/services/scroll-position.service';
import { SocketService } from './../../../../core/services/socket.service';
import { TokenUsageService } from './../../../../core/services/token-usage.service';
import { UserService } from './../../../../core/services/user.service';
import { MedicationAutocompleteComponent } from './../../../../shared/components/medication-autocomplete/medication-autocomplete.component';
import { ProcedureCodeSelectorComponent } from './../../../../shared/components/procedure-code-selector/procedure-code-selector.component';
import { LabTestSelectorComponent, LabTestChecklistItem } from './../../../../shared/components/lab-test-selector/lab-test-selector.component';
import { LabOrderConfigDialogComponent } from './../../../../shared/components/lab-order-config-dialog/lab-order-config-dialog.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { PatientSatisfactionSurveyService } from 'src/app/core/services/patient-satisfaction-survey.service';
import { AppointmentTypeSurveyTemplateService } from 'src/app/core/services/appointment-type-survey-template.service';
// ── Risk management (workstream E of tasks/risk-management.md) ────────────────────────────
import { PatientRiskItemService } from './../../../../core/services/patient-risk-item.service';
import { HospitalService } from './../../../../core/services/hospital.service';
import { RiskItemAddDialogComponent, RiskItemAddDialogData, RiskItemAddDialogResult } from './../../../../shared/components/risk-suggestion-list/risk-item-add-dialog.component';
import { RiskSuggestion, normaliseRiskSuggestions, pickEncounterAppointment, predictRiskFacility, riskResolutionLabel } from './../../../../shared/components/risk-suggestion-list/risk-suggestion.model';
@Component({
  selector: 'app-patient-ai-request-detail',
  templateUrl: './patient-ai-request-detail.component.html',
  styleUrls: ['./patient-ai-request-detail.component.scss']
})
export class PatientAiRequestDetailComponent extends BaseComponent implements OnInit, OnDestroy {
  /** Marks where this work starts or finishes, for REMOAT's usage report. */
  private readonly usage = inject(UsageService);

  // ── ICD-10 coding-rule context (shared/util/icd-coding-context.util.ts) ──────────
  //
  // Feeds the code selector's rules. Each returns null when the value is unknown, and a rule
  // with no context stays SILENT rather than guessing — a false alarm reads as the system not
  // knowing the patient, which costs trust in every other finding.
  get icdCodingSex(): string | null { return sexFromPatient(this.patient); }
  get icdCodingAge(): number | null { return ageFromDob(this.patient?.dob); }

  /** Dates a newly added code from the admission this request was raised against — never from today. */
  get icdDefaultOnsetDate(): string | null { return toOnsetDate(this.patient?.admissionDate); }

  @ViewChildren(MatMenuTrigger) menuTriggers!: QueryList<MatMenuTrigger>;

  private subscriptions: Subscription[] = [];

  /* TODO: remove placeholders inserted for information from all AI generations */

  todayDate: moment.Moment = moment();

  addingNote: any = {};
  @ViewChild('addNoteDialog') addNoteDialog?: TemplateRef<any>;

  addingTask: any = {};

  /**
   * Every free-text field find & replace should span. Bound to
   * [appFindReplaceScope] on the container, so "All fields" reaches the letters
   * even while their sections are collapsed or hidden by the dynamic layout —
   * which is the point: the letters are generated from the note and inherit its
   * misspellings.
   *
   * Deliberately excluded: the *Prompt controls (instructions to the AI, not
   * clinical content) and the medication list, whose entries are catalogue-backed
   * records rather than text.
   */
  /**
   * Rebuilt only when initForms() swaps the FormGroups out, since the binding is
   * read on every change-detection pass and the controls themselves are stable.
   */
  private findReplaceTargetsCache: FindReplaceTarget[] = [];
  private findReplaceTargetsFor: FormGroup | null = null;

  get findReplaceTargets(): FindReplaceTarget[] {
    if (!this.notesForm) {
      return [];
    }
    if (this.findReplaceTargetsFor === this.notesForm) {
      return this.findReplaceTargetsCache;
    }

    // Reads live off addingTask, which is replaced wholesale when a task opens.
    const task: FindReplaceTarget = {
      label: 'New task notes',
      read: () => this.addingTask?.notes ?? '',
      write: value => (this.addingTask.notes = value)
    };

    this.findReplaceTargetsCache = targets(
      controlTarget('Notes', this.notesForm.get('notes')),
      controlTarget('Note title', this.notesForm.get('suggestedNoteTitle')),
      controlTarget('Diagnosis', this.diagnosisForm?.get('diagnosis') ?? null),
      controlTarget('Radiology request', this.radiologyForm?.get('radiologyRequest') ?? null),
      controlTarget('Letter of motivation', this.lomForm?.get('letterOfMotivation') ?? null),
      controlTarget('Motivation reason', this.lomForm?.get('motivationReason') ?? null),
      controlTarget('Letter to colleague', this.colleagueForm?.get('letterToColleague') ?? null),
      controlTarget('Letter reason', this.colleagueForm?.get('letterReason') ?? null),
      controlTarget('Letter to GP', this.gpForm?.get('letterToGP') ?? null),
      controlTarget('Letter to patient', this.patientForm?.get('letterToPatient') ?? null),
      controlTarget("Letter to patient's employer", this.patientWorkForm?.get('letterToPatientWork') ?? null),
      controlTarget('Patient report', this.reportForm?.get('patientReport') ?? null),
      controlTarget('Lab order notes', this.labTestsForm?.get('labOrderNotes') ?? null),
      task
    );
    this.findReplaceTargetsFor = this.notesForm;
    return this.findReplaceTargetsCache;
  }


  @ViewChild('procedureCodeSelector') procedureCodeSelector!: ProcedureCodeSelectorComponent;
  @ViewChild('dialogProcedureCodeSelector') dialogProcedureCodeSelector!: ProcedureCodeSelectorComponent;
  @ViewChild('icdCodeSelector') icdCodeSelector!: IcdCodeSelectorComponent;
  @ViewChild('motivationProcedureCodeSelector') motivationProcedureCodeSelector!: ProcedureCodeSelectorComponent;

  hideAllergies: boolean = false;

  @ViewChild('notesTextarea', { static: false }) notesTextarea?: ElementRef;
  @ViewChild('patientReportTextarea', { static: false }) patientReportTextarea?: ElementRef;
  @ViewChild('letterOfMotivationTextarea', { static: false }) letterOfMotivationTextarea?: ElementRef;
  @ViewChild('radiologyRequestTextarea', { static: false }) radiologyRequestTextarea?: ElementRef;
  @ViewChild('letterToColleagueTextarea', { static: false }) letterToColleagueTextarea?: ElementRef;
  @ViewChild('letterToGPTextarea', { static: false }) letterToGPTextarea?: ElementRef;
  @ViewChild('letterToPatientTextarea', { static: false }) letterToPatientTextarea?: ElementRef;
  @ViewChild('letterToPatientWorkTextarea', { static: false }) letterToPatientWorkTextarea?: ElementRef;
  @ViewChild('dischargeSummaryTextarea', { static: false }) dischargeSummaryTextarea?: ElementRef;

  @ViewChildren(CdkTextareaAutosize) autosizes!: QueryList<CdkTextareaAutosize>;

  @ViewChild('selectedTimelineItems') selectedTimelineItems?: MatSelectionList;

  @ViewChild('medicationAutocomplete') medicationAutocomplete?: MedicationAutocompleteComponent;

  // Lab test selector properties
  suggestedLabTestChecklistItems: LabTestChecklistItem[] = [];
  selectedLabTests: LabTestChecklistItem[] = []; // Unified list for both bundle and searched tests
  userPreferredLabTests: any[] = [];
  labTestBundles: any[] = [];
  selectedBundles: any[] = [];
  previousSelectedBundles: any[] = []; // Track previous selection to detect deselected bundles
  allOtherTests: LabTestChecklistItem[] = [];
  showIndividualTests: boolean = true; // Always show search by default

  //@Input() aiRequest: {title: string; [key: string]: any};
  darkTheme!: NgxMaterialTimepickerTheme;
  diagnosisForm!: FormGroup;
  notesForm!: FormGroup;
  tasksForm!: FormGroup;
  /** E2. Holds `suggestedRiskItems` only — everything else about an item is decided in E3's dialog. */
  riskForm!: FormGroup;
  medicationForm!: FormGroup;
  labTestsForm!: FormGroup;
  reportForm!: FormGroup;
  lomForm!: FormGroup;
  radiologyForm!: FormGroup;
  colleagueForm!: FormGroup;
  gpForm!: FormGroup;
  patientForm!: FormGroup;
  patientWorkForm!: FormGroup;
  //dischargeForm!: FormGroup;
  consentForm!: FormGroup;


  visibleDiagnosisFields: string[] = [];
  editingRequest: any;
  selectedConsentForm: any;
  patient!: any;
  patientName!: string;
  selectedGroup!: any;

  /** A state unit (tasks/state-mode.md): nothing is sent from here, no lab orders, no billing flags. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  // Physician selection for non-doctor users (lab order section)
  labOrderGroupUsers: any[] = [];
  selectedLabOrderPhysicianId: string | null = null;
  showLabOrderPhysicianSelection: boolean = false;
  get isNonDoctorUser(): boolean {
    return this.userProfile?.userType !== 'doctor';
  }
  get hasSelectedLabOrderPhysician(): boolean {
    return !this.isNonDoctorUser || !!this.selectedLabOrderPhysicianId;
  }

  get selectedLabOrderPhysician(): any {
    if (!this.isNonDoctorUser || !this.selectedLabOrderPhysicianId) {
      return this.userProfile;
    }
    return this.labOrderGroupUsers.find(u => u.userId === this.selectedLabOrderPhysicianId) || null;
  }

  // Getter to check if lab order can be submitted
  get canSubmitLabOrder(): boolean {
    // For non-doctor users, check the selected physician's lancetCode
    // For doctor users, check their own lancetCode
    const physician = this.selectedLabOrderPhysician;
    const lancetCode = physician?.lancetCode;
    const hasLancetCode = lancetCode && typeof lancetCode === 'string' && lancetCode.trim() !== '';
    return !!hasLancetCode;
  }
  noteTypes: any[] = [];
  consultNoteTypes: any[] = [];
  procedureNoteTypes: any[] = [];
  codingBundles: any[] = [];
  colleagues: any[] = [];
  medicalTeam: any[] = [];
  tasks: any[] = [];
  /** E4/E5 — every risk item on THIS patient (the service is patient-scoped, J1). */
  riskItems: any[] = [];
  /** E3 — the practice's hospitals, with their wards, for the facility preview only. */
  hospitals: any[] = [];
  /**
   * E3-1 — this patient's `PatientAdmissionHistory` rows, for the facility preview only.
   *
   * Already loaded in the patient file, which is the whole reason §4 branch 3 can now run in the
   * browser instead of being deferred to the POST. Read-only here; the server still resolves the
   * facility that is actually stored.
   */
  admissionRecords: any[] = [];
  /** Guards double-submits while a create is in flight. */
  addingRiskItem: boolean = false;
  allergies: any[] = [];
  filteredColleagues$!: Observable<any[]>;
  filteredMedicalTeam$!: Observable<any[]>;
  consentTypes: any[] = [];
  labOrderPriorities: any[] = [
    { id: 'routine', label: 'Routine' },
    { id: 'urgent', label: 'Urgent' },
    { id: 'stat', label: 'Stat' }
  ];
  private socketSubs: Subscription[] = [];
  //timelineData?: any[];
  //filteredTimelineData?: any[] = [];

  addingColleague: any = {}; // Object to store the new colleague being added
  @ViewChild('addColleagueDialog') addColleagueDialog?: TemplateRef<any>;
  titles = TITLES;
  specialities = SPECIALITIES;


  private routeParamsSub!: Subscription;

  selectedProcedureCodes: any[] = [];
  selectedMotivationProcedureCodes: any[] = [];
  selectedMotivationIcdCodes: any[] = [];
  selectedPatientIcdCodes: any[] = [];
  patientNotesForThisAIRequest: any[] = [];
  patientConsentsForThisAIRequest: any[] = [];

  /**
   * §29 - the attachment picker's selection, posted with the consent request.
   *
   * MIRRORS `patient-consent-detail`, which is the manual "Add Patient Consent" screen. Both send
   * the same request through the same endpoint, and until now only one of them could attach the
   * practice's documents to it - so a clinician who built a consent from an AI request had no way
   * to send the pre-admission paperwork with it and had to raise a second, separate send.
   *
   * ABSENT vs EMPTY is load-bearing on the server (`createConsentInvite`): `undefined` means no
   * picker was involved and the practice's `sendWithInvite` configuration decides, while `[]`
   * means the user unticked everything and nothing is raised. This is always an array, because
   * this screen always has the picker - so an untick here genuinely means "do not send it".
   */
  selectedConsentDocumentIds: string[] = [];
  labOrderForThisAIRequest: any = null;

  tokenBalance?: number;
  lockProcessing: boolean = false;
  isPremium: boolean = false;

  selectedAllergies: any[] = [];
  noAllergies: boolean = false;
  selectedMedicalHistory: any[] = [];
  noMedicalHistory: boolean = false;
  selectedMedicationHistory: any[] = [];
  noMedicationHistory: boolean = false;

  groupUsers: any[] = [];
  showProviderSelection: boolean = false;
  selectedProviderId: string | null = null;

  userProfile: any;
  canSign: boolean = false;
  isPhysician: boolean = false;
  hasSignaturePermission: boolean = false;

  private appointments?: any[];

  // Diagnosis/ICD-10 Dialog properties
  @ViewChild('diagnosisIcdDialog') diagnosisIcdDialog?: TemplateRef<any>;
  @ViewChild('icdCodeSelectorDialog') icdCodeSelectorDialog!: IcdCodeSelectorComponent;
  diagnosisIcdForm!: FormGroup;
  visibleDiagnosisFieldsDialog: string[] = [];
  selectedPatientIcdCodesDialog: any[] = [];
  private diagnosisIcdDialogRef!: MatDialogRef<any>;
  /** The patient the diagnosis/ICD-10 dialog was opened for; its save goes to them. */
  private diagnosisDialogPatient: any = null;
  private skipDiagnosisCheck: boolean = false;

  useExperimentalFeatures: boolean = localStorage.getItem('experimentalFeaturesEnabled') === 'true';
  useDynamicLayout: boolean = false;

  visibleSections = {
    details: false,
    notes: true,
    fileUpdate: false,
    medication: false,
    labTests: false,
    tasks: false,
    // E1 — the RISK MANAGEMENT section. Hidden until there is something in it (E5).
    riskManagement: false,
    consent: false,
    lom: false,
    radiologyRequest: false,
    letterToColleague: false,
    letterToGP: false,
    letterToPatient: false,
    letterToPatientWork: false,
  }

  completedSections = {
    details: false,
    notes: false,
    fileUpdate: false,
    medication: false,
    labTests: false,
    tasks: false,
    // E1 — set once at least one issue has been confirmed onto the register from this request.
    riskManagement: false,
    consent: false,
    lom: false,
    radiologyRequest: false,
    letterToColleague: false,
    letterToGP: false,
    letterToPatient: false,
    letterToPatientWork: false,
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private groupService: GroupService,
    private patientService: PatientService,
    private aiService: PatientAiService,
    private noteTypeService: NoteTypeService,
    private codingBundleService: CodingBundleService,
    private cd: ChangeDetectorRef,
    private pdfDownloadService: PdfDownloadService,
    private noteService: PatientNoteService,
    private colleagueService: ColleagueService,
    private taskService: PatientTaskService,
    private scrollPositionService: ScrollPositionService,
    private socketService: SocketService,
    private patientCommunicationService: PatientCommunicationService,
    private consentFormsService: ConsentFormsService,
    private consentService: PatientConsentService,
    private inviteService: PatientInviteService,
    private patientNoteService: PatientNoteService,
    private patientAdmissionHistoryService: PatientAdmissionHistoryService,
    private usageService: TokenUsageService,
    private analyticsService: AnalyticsService,
    private speechRecognitionService: SpeechRecognitionService,
    private patientAppointmentService: PatientAppointmentService,
    private themeService: ThemeService,
    private patientLabService: PatientLabService,
    private surveyService: PatientSatisfactionSurveyService,
    private appointmentTypeSurveyTemplateService: AppointmentTypeSurveyTemplateService,
    private plannedProcedureCodeService: PlannedProcedureCodeService,
    private theatreTeamService: TheatreTeamService,
    private codePromptingService: CodePromptingService,
    private disciplineConfigService: DisciplineConfigService,
    // Workstream E. `patientRiskItemService` is the PATIENT-scoped twin (J1) — the group-scoped
    // `RiskItemService` (G1) belongs to the register, not to the patient file. `hospitalService`
    // is here only to name the patient's hospital/ward in E3's dialog; the authority on the
    // facility remains the server-side resolver.
    private patientRiskItemService: PatientRiskItemService,
    private hospitalService: HospitalService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }


  ngOnInit() {
    this.initForms();
    //this.loadTimelineData();

    this.useDynamicLayout = !(document.body.classList.contains('mobile') || document.body.classList.contains('tablet'));

    this.routeParamsSub = this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.aiService.getPatientAIRequestById(id)
          .pipe(takeUntil(this.destroy$)).subscribe();
      } else {
        this.resetForm();
      }
    });

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        this.userProfile = profile;
        this.isPhysician = this.userService.isUserPhysician;
        this.canSign = this.userService.canUserSign;
        this.hasSignaturePermission = this.userService.isNonPhysician;
      });

    // Load group users for physician selection in lab order section
    this.loadLabOrderGroupUsers();

    // Subscribe to user's preferred lab tests (only if user is a doctor or physician is selected)
    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        // Only load if user is a doctor or if a physician has been selected
        if (!this.isNonDoctorUser || this.selectedLabOrderPhysicianId) {
          this.loadLabTestBundles(profile);
        }
      });

    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            // Check if the selected group's speciality is surgical or medical
            let surgicalMotivationLetter = true;
            if (checkIfGroupIsSurgical(selectedGroup.groupSpeciality)) {
              surgicalMotivationLetter = true;
            } else if (checkIfGroupIsMedical(selectedGroup.groupSpeciality)) {
              surgicalMotivationLetter = false;
            }
            this.lomForm.patchValue({ surgicalMotivationLetter: surgicalMotivationLetter });
            this.selectedGroup = selectedGroup;
            this.isPremium = selectedGroup.isPremium === true;
            this.notesForm.patchValue({ groupId: patientGroupId(this.patient, this.selectedGroup.id) });
            this.consentForm.patchValue({ groupId: patientGroupId(this.patient, this.selectedGroup.id) });
            // Update lockProcessing - always false if premium
            if (this.isPremium) {
              this.lockProcessing = false;
            } else if (this.usageService.tokenBalanceSnapshot < 0) {
              this.lockProcessing = true;
            } else {
              this.lockProcessing = false;
            }
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();


    this.patientService.currentPatient$
      .pipe(
        switchMap(selectedPatient => {
          if (selectedPatient) {

            this.patientName = `${selectedPatient.title ? selectedPatient.title + '_' : ''}${selectedPatient.name}_${selectedPatient.surname}`;
            this.patient = selectedPatient;

            this.diagnosisForm.patchValue({ diagnosis: this.patient.diagnosis, weight: this.patient.weight, height: this.patient.height });
            // Patch the diagnosis fields dynamically
            for (let i = 2; i <= 24; i++) {
              const fieldName = `diagnosis${i}`;
              if (this.patient[fieldName]) {
                this.addDiagnosisField(this.patient[fieldName]);
              }
            }


            this.notesForm.patchValue({
              patientId: this.patient.id,
              groupId: patientGroupId(this.patient, this.selectedGroup?.id),
            });

            this.consentForm.patchValue({
              patientId: this.patient.id,
              groupId: patientGroupId(this.patient, this.selectedGroup?.id),
            });

            this.patientForm.patchValue({
              patientEmail: this.patient.email,
            });

            this.patientWorkForm.patchValue({
              patientEmployer: this.patient.employer,
            });


            this.medicationForm.patchValue({
              name: this.patient.name,
              email: this.patient.email,
            });

            this.gpForm.patchValue({
              generalPractitioner: this.patient.generalPractitioner,
              generalPractitionerEmail: this.patient.generalPractitionerEmail,
              generalPractitionerPhone: this.patient.generalPractitionerPhone,
              medicalAid: medicalAidDisplay(this.patient).name,
              medicalAidNumber: this.patient.medicalAidNumber,
              medicalAidPlan: medicalAidDisplay(this.patient).plan
            });


            this.lomForm.patchValue({
              medicalAid: medicalAidDisplay(this.patient).name,
              medicalAidNumber: this.patient.medicalAidNumber,
              medicalAidPlan: medicalAidDisplay(this.patient).plan
            });

            /* if (this.patient.admissionDate) {
              this.dischargeForm.patchValue({ admissionDate: moment(this.patient.admissionDate, 'DD/MM/YYYY') });
            } */

            if (this.patient.icdCodes) {
              this.selectedPatientIcdCodes = this.patient.icdCodes;
              this.onIcdCodesChanged(this.patient.icdCodes);
            }

            if (this.patient.medicalTeam) {
              this.medicalTeam = this.patient.medicalTeam;
            }

            if (this.patient.allergies) {
              this.selectedAllergies = this.patient.allergies;
              this.onAllergiesChanged(this.patient.allergies);
            }
            this.noAllergies = this.patient.noAllergies;


            if (this.patient.medicalHistory) {
              this.selectedMedicalHistory = this.patient.medicalHistory;
              this.onMedicalHistoryChanged(this.patient.medicalHistory);
            }
            this.noMedicalHistory = this.patient.noMedicalHistory;

            if (this.patient.medicationHistory) {
              this.selectedMedicationHistory = this.patient.medicationHistory;
              this.onMedicationHistoryChanged(this.patient.medicationHistory);
            }
            this.noMedicationHistory = this.patient.noMedicationHistory;

          } else {
            this.returnToParent(false);
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.filteredMedicalTeam$ = this.gpForm.get('generalPractitioner')!.valueChanges.pipe(
      startWith(''),
      switchMap(value => this.filterMedicalTeam(value))
    );


    this.aiService.editingAIRequest$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.editingRequest = data;
        if (data) {
          this.patchForm();
        }
      });

    this.aiService.incrementalUpdates$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        /* DATA COULD INCLUDE ANY OF THE FOLLOWING:
            - suggestedProcedureCodes
            - suggestedModifiers
            - allergies
            - suggestedICD10Codes
            - detectedMedicalHistory
            - detectedMedications
            - suggestedMedication
            - suggestedAdminTasks
            - detectedProcedureTitle
            - detectedCondition
            - detectedRisks
            - motivationCodingBundleId
            - suggestedMotivationProcedureCodes
            - startTime
            - finishTime
            - codingBundleId
            */

        if (data && data.id === this.editingRequest.id) {
          this.handleIncrementalUpdate(data);
        }
      });

    this.noteTypeService.noteTypes$
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe((noteTypes) => {
        if (noteTypes) {
          // Filter out forCura note types from regular note type selections (only show forAi note types)
          const filteredNoteTypes = this.updateNoteTypesStructure(noteTypes.filter((nt: any) => nt.forAi && !nt.forCura));
          this.noteTypes = filteredNoteTypes;

          // Split and sort alphabetically
          this.consultNoteTypes = _.sortBy(filteredNoteTypes.filter((nt: any) => !nt.isProcedure), ['label']);
          this.procedureNoteTypes = _.sortBy(filteredNoteTypes.filter((nt: any) => nt.isProcedure), ['label']);
        }
      });


    this.codingBundleService.codingBundles$
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe((codingBundles) => {
        if (codingBundles) {
          this.codingBundles = _.sortBy(codingBundles, 'label');
          const selectedCodingBundle = this.codingBundles.filter(nt => nt.id === this.editingRequest.codingBundleId)[0];
          this.selectedProcedureCodes = selectedCodingBundle?.procedureCodes.map((c: any) => { return { ...c, paidCash: selectedCodingBundle.paidCash, noCharge: selectedCodingBundle.noCharge } }) || [];
          this.notesForm.patchValue({ paidCash: selectedCodingBundle.paidCash, noCharge: selectedCodingBundle.noCharge });
          if (this.editingRequest.motivationCodingBundleId) {
            const selectedMotivationCodingBundle = this.codingBundles.filter(nt => nt.id === this.editingRequest.motivationCodingBundleId)[0];
            this.selectedMotivationProcedureCodes = selectedMotivationCodingBundle?.procedureCodes.map((c: any) => { return { ...c, paidCash: selectedMotivationCodingBundle.paidCash, noCharge: selectedMotivationCodingBundle.noCharge } }) || [];
          }
          // The bundle is only the starting point. If the request already carries the codes that
          // were on its letter, those win — and either way the form control has to be told (§1.1).
          this.restoreMotivationProcedureCodes();
        }
      });

    this.colleagueService.colleagues$
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe(colleagues => {
        if (colleagues) {
          this.colleagues = colleagues;
        }
      });

    this.filteredColleagues$ = this.colleagueForm.get('colleagueName')!.valueChanges.pipe(
      startWith(''),
      switchMap(value => this.filterColleagues(value))
    );

    this.taskService.patientTasks$
      .pipe(takeUntil(this.destroy$))
      .subscribe(tasks => {
        if (tasks) {
          this.tasks = tasks;
        }
      });

    // E4/E5 — the patient's already-confirmed risk items, for the dedupe guard and for keeping
    // the section on screen once something has been added even if the suggestion list is empty.
    this.patientRiskItemService.patientRiskItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        if (items) {
          this.riskItems = items;
          if (this.riskItemsForThisAIRequest.length) {
            // The green tick only. NOT `visibleSections` — revealing a section the user has
            // not asked for is what TASK LIST deliberately does not do, and this one was the
            // odd one out. The sidebar entry is how a section is opened; `scrollTo` calls
            // `setVisibleSection`.
            this.completedSections.riskManagement = true;
          }
        }
      });

    // E3 — names the hospital/ward in the confirm dialog. `hospitals$` seeds NULL and stays NULL
    // when the fetch fails (deliberately: an empty array would be a claim that the practice has no
    // hospitals), so this only ever assigns a real list.
    this.hospitalService.hospitals$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hospitals => {
        if (hospitals) {
          this.hospitals = hospitals;
        }
      });

    // E3-1 — §4 BRANCH 3 IN THE BROWSER. These rows are already loaded for this patient, so the
    // preview can match an admission covering the issue date instead of falling to "in rooms" and
    // waiting for the POST to contradict it.
    //
    // Unlike `hospitals$`, this subject emits `[]` both for "this patient has no admissions" and
    // for "the fetch failed after its retries" — the service's own `catchError` returns
    // `{ data: [] }` to clear stale data. The two are indistinguishable here, which is exactly
    // why an empty result leaves the preview PROVISIONAL rather than asserting an in-rooms issue.
    this.patientAdmissionHistoryService.patientAdmissionRecords$
      .pipe(takeUntil(this.destroy$))
      .subscribe(records => {
        this.admissionRecords = Array.isArray(records) ? records : [];
      });

    this.noteService.patientNotes$
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe(notes => {
        if (notes) {
          this.patientNotesForThisAIRequest = notes.filter(n => n.aiRequestId === this.editingRequest.id);
        }
      });

    this.consentFormsService.consentForms$
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe((consentForms) => {
        if (consentForms) {
          this.consentTypes = consentForms;
        }
      });

    this.consentService.patientConsents$
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe(consents => {
        if (consents) {
          this.patientConsentsForThisAIRequest = consents.filter(c => c.aiRequestId === this.editingRequest.id);
        }
      });

    this.usageService.tokenBalance$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.tokenBalance = result;
        // Update lockProcessing - always false if premium
        if (!this.isPremium) {
          if (this.usageService.tokenBalanceSnapshot < 0) {
            this.lockProcessing = true;
          } else {
            this.lockProcessing = false;
          }
        }
      });

    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        this.groupUsers = users?.filter(u => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50');
        const currentUser = this.userService.user;
        if (this.groupUsers.findIndex(gu => gu.userId === currentUser.id) > -1) {
          this.consentForm.patchValue({ physicianId: currentUser.id });
        } else {
          this.consentForm.patchValue({ physicianId: this.groupUsers[0].userId })
        }

        // Determine provider selection logic for procedure codes
        this.determineProviderSelection();
      });

    this.scrollPositionService.scrollTo('top', true);

    // Subscribe to detected commands from speech recognition
    this.subscriptions.push(
      this.speechRecognitionService.getDetectedCommand().subscribe((commandData) => {
        this.handleVoiceCommand(commandData.command, commandData.payload);
      })
    );


    this.subscriptions.push(
      this.patientAppointmentService.patientAppointments$
        .subscribe(result => { if (result) this.appointments = result })
    );

    this.themeService.isDarkMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDarkMode) => {
        this.darkTheme = this.themeService.getTimepickerTheme(isDarkMode);
      });

  }

  /**
   * Determines the provider selection logic based on the user's requirements:
   * - If only one provider in the group, use that as default and don't show selection
   * - If multiple providers and logged-in user is a physician, default to them
   * - Otherwise (multiple providers and logged-in user is not a physician), default to group owner and show dropdown
   */
  private determineProviderSelection(): void {
    if (this.groupUsers.length === 0) {
      this.selectedProviderId = null;
      this.showProviderSelection = false;
      return;
    }

    if (this.groupUsers.length === 1) {
      // Only one provider - use as default and don't show selection
      this.selectedProviderId = this.groupUsers[0].userId;
      this.showProviderSelection = false;
      return;
    }

    // Multiple providers - check if current user is a physician
    const currentUser = this.userService.user;
    const isCurrentUserPhysician = this.groupUsers.findIndex(gu => gu.userId === currentUser.id) > -1;

    if (isCurrentUserPhysician) {
      // Current user is a physician - default to them
      this.selectedProviderId = currentUser.id;
      this.showProviderSelection = false;
    } else {
      // Current user is not a physician - default to group owner and show dropdown
      const groupOwner = this.groupUsers.find(user => user.userId === this.groupService.group?.userId) || this.groupUsers[0];
      this.selectedProviderId = groupOwner.userId;
      this.showProviderSelection = true;
    }
  }

  private handleVoiceCommand(command: string, payload?: any): void {
    switch (command) {
      case 'CLOSE_FILE':
        console.log('Closing patient file...');
        this.returnToParent();
        break;
      case 'SCROLL_TO':
        console.log('scroll to section:', payload);
        // `payload` here is expected to be the matched section `id` provided by `SpeechRecognitionService`
        if (payload) {
          this.scrollTo(payload); // Scroll to the identified section
        } else {
          console.warn('No matching section found for scrolling.');
        }
        break;
      case 'GENERATE_SECTION':
        console.log('generate:', payload);

        // Check if generation is allowed for this section
        if (['top', 'pc', 'file_update', 'medication', 'tasks', 'consent'].includes(payload)) return;

        // Scroll to the section
        this.scrollTo(payload);

        // Map to the correct function for generation
        switch (payload) {
          case 'lom':
            this.aiGenerateLetterOfMotivation();
            break;
          case 'letter_to_colleague':
            this.aiGenerateLetterToColleague();
            break;
          case 'letter_to_gp':
            this.aiGenerateLetterToGP();
            break;
          case 'letter_to_patient':
            this.aiGenerateLetterToPatient();
            break;
          case 'letter_to_patient_employer':
            this.aiGenerateLetterToPatientWork();
            break;
        }
        break;
    }
  }


  get userRole(): string {
    return this.selectedGroup?.userRole;
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.routeParamsSub) {
      this.routeParamsSub.unsubscribe();
    }
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private updateNoteTypesStructure(noteTypes: any) {
    for (let nt of noteTypes) {
      if (this.noteTypeService.areHeadingsStrings(nt.headings)) {
        nt.headings = this.noteTypeService.convertHeadingsToObjects(nt.headings);
      }
    }
    return noteTypes;
  }

  initForms() {

    this.notesForm = this.fb.group({
      groupId: ['', Validators.required],
      patientId: ['', Validators.required],
      noteTypeId: [''],
      suggestedNoteTitle: ['', Validators.required],
      notes: ['', Validators.required],
      notesPrompt: [''],
      codingBundleId: [''],
      paidCash: [false],
      noCharge: [false],
      suggestedProcedureCodes: [''],
      suggestedICD10Codes: [''],
      detectedMedicalHistory: [''],
      detectedMedications: [''],
      allergies: [''],
      startTime: [''],
      finishTime: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()],
    });

    this.diagnosisForm = this.fb.group({
      //diagnosis: ['', Validators.required],
      diagnosis: [''], //TODO: removed the required attribute here to allow AI requests to saved on patients whos status is still 'invite'
      weight: [''],
      height: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()],
    });

    this.tasksForm = this.fb.group({
      suggestedAdminTasks: [''],
    });

    // E2. `suggestedRiskItems` is NULL — not `[]` — on every request predating this feature and
    // every recording where the extractor found nothing, so the control is routinely null and the
    // template must never dereference `.items` off it directly.
    this.riskForm = this.fb.group({
      suggestedRiskItems: [null],
    });

    this.medicationForm = this.fb.group({
      suggestedMedication: [''],
      name: [''],
      email: [''],
      ccEmail: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()],
    });

    this.labTestsForm = this.fb.group({
      suggestedLabTests: [''],
      orderDate: [moment(), Validators.required],
      priority: ['routine', Validators.required],
      labOrderNotes: [''],
      selectedBundles: [[]]
    });

    this.reportForm = this.fb.group({
      startDate: [moment().subtract(7, 'days')],
      endDate: [moment()],
      timelineIds: [[]],
      noteTypeId: [''],
      patientReport: [''],
      patientReportPrompt: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()],
    });

    this.lomForm = this.fb.group({
      medicalAid: [''],
      medicalAidNumber: [''],
      medicalAidPlan: [''],
      motivationCodingBundleId: [''],
      suggestedMotivationProcedureCodes: [''],
      // The codes the user actually SELECTED for the letter, as opposed to
      // `suggestedMotivationProcedureCodes` directly above, which is the AI's read-only
      // first guess in a different shape ({codes:[{code,description,score,reason}]}).
      //
      // It is a form control purely so `getAllDetails()` picks it up — before this existed the
      // selection lived only in the `selectedMotivationProcedureCodes` class field, so Save
      // Request never sent it and the server never stored it
      // (tasks/theatre-booking-and-procedure-lifecycle.md §1.1).
      //
      // There is no control on screen bound to this name: `app-procedure-code-selector` is not
      // a ControlValueAccessor, so it stays bound to the class field and every writer of that
      // field calls `syncMotivationProcedureCodesToForm()` to mirror it here.
      motivationProcedureCodes: [[]],
      letterOfMotivation: [''],
      letterOfMotivationPrompt: [''],
      surgicalMotivationLetter: [true],
      motivationReason: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()],
    });

    this.colleagueForm = this.fb.group({
      colleagueName: [''],
      colleagueSpeciality: [''],
      colleagueEmail: [''],
      selectedColleague: [''],
      letterToColleague: [''],
      letterToColleaguePrompt: [''],
      letterReason: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()],
    });

    this.radiologyForm = this.fb.group({
      radiologyRequest: [''],
      radiologyRequestPrompt: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()],
    });

    this.gpForm = this.fb.group({
      generalPractitioner: [''],
      generalPractitionerEmail: [''],
      generalPractitionerPhone: [''],
      letterToGP: [''],
      letterToGPPrompt: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()],
    });

    this.patientForm = this.fb.group({
      patientEmail: [''],
      letterToPatient: [''],
      letterToPatientPrompt: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()],
    });

    this.patientWorkForm = this.fb.group({
      patientEmployer: [''],
      letterToPatientWork: [''],
      letterToPatientWorkPrompt: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()],
    });

    /*  this.dischargeForm = this.fb.group({
       admissionDate: [''],
       dischargeDate: [moment()],
       dischargeSummary: [''],
       dischargeSummaryPrompt: [''],
       includeSignatureOnPdf: [false],
       includeSignatureOnEmail: [false],
       selectedDoctor: [null],
     }); */

    this.consentForm = this.fb.group({
      date: [moment()],
      groupId: [''],
      patientId: [''],
      detectedProcedureTitle: [''],
      detectedCondition: [''],
      detectedRisks: [''],
      consentType: [''],
      consentFormId: [''],
      physicianId: [''],
      consentGiven: [true],
    });

    this.diagnosisIcdForm = this.fb.group({
      diagnosis: ['', Validators.required],
    }, { validators: this.diagnosisIcdValidator });
  }

  // Custom validator to ensure both diagnosis and ICD codes are present
  private diagnosisIcdValidator = (group: FormGroup): { [key: string]: any } | null => {
    const diagnosis = group.get('diagnosis')?.value;
    const hasDiagnosis = diagnosis && diagnosis.trim() !== '';
    const hasIcdCodes = this.selectedPatientIcdCodesDialog && this.selectedPatientIcdCodesDialog.length > 0;

    if (!hasDiagnosis || !hasIcdCodes) {
      return { 'diagnosisIcdRequired': true };
    }

    return null;
  }

  patchForm() {
    if (!this.editingRequest) {
      return; // Guard clause in case this.editingRequest is not yet set
    }

    // Restore persisted section states if they exist
    if (this.editingRequest.completedSections) {
      this.completedSections = { ...this.completedSections, ...this.editingRequest.completedSections };
    }
    if (this.editingRequest.visibleSections) {
      this.visibleSections = { ...this.visibleSections, ...this.editingRequest.visibleSections };
    }

    this.determineCompletedSections(this.editingRequest);

    // Basic patching for directly matched fields
    this.notesForm.patchValue(this.editingRequest);
    this.tasksForm.patchValue(this.editingRequest);
    // E2. Null-safe by construction: `patchValue` sets the control to whatever the column holds,
    // including null, and `normaliseRiskSuggestions` reads it.
    this.riskForm.patchValue(this.editingRequest);
    this.medicationForm.patchValue(this.editingRequest);

    // For lab tests, if there's an existing lab order and user is non-doctor, 
    // we need to load the physician and bundles first
    if (this.editingRequest.id && this.isNonDoctorUser) {
      this.loadExistingLabOrderForAIRequest();
    } else {
      this.labTestsForm.patchValue(this.editingRequest);
    }
    this.lomForm.patchValue(this.editingRequest);
    // Re-opening a request restores the codes that were actually ON its letter of motivation,
    // in preference to re-expanding `motivationCodingBundleId`. `patchValue` above has already
    // put `motivationProcedureCodes` on the control (and `suggestedMotivationProcedureCodes`,
    // untouched, on its own); this drives the on-screen selector, which reads the class field.
    // §1.1 — before this, what came back was the AI's first guess, not what was motivated for.
    this.restoreMotivationProcedureCodes();
    this.radiologyForm.patchValue(this.editingRequest);
    this.reportForm.patchValue(this.editingRequest);
    this.colleagueForm.patchValue(this.editingRequest);
    this.gpForm.patchValue(this.editingRequest);
    this.patientForm.patchValue(this.editingRequest);
    this.patientWorkForm.patchValue(this.editingRequest);
    this.consentForm.patchValue(this.editingRequest);
    //this.dischargeForm.patchValue(this.editingRequest);

    if (this.editingRequest.allergies) {
      const uniqueAllergies = this.editingRequest.allergies.filter(
        (allergy: any) => !this.patient.allergies.some((patientAllergy: any) => patientAllergy.id === allergy.id)
      );
      this.notesForm.patchValue({ allergies: uniqueAllergies });
      this.allergies = uniqueAllergies;
    }
    if (this.selectedGroup?.autoGenerateSickNotes && !this.editingRequest.letterToPatientWork && !this.patientWorkForm.get('letterToPatientWork')?.value && !this.isGeneratingSickLetter) {
      this.aiGenerateLetterToPatientWork();
    }

  }

  private determineCompletedSections(request: any): void {
    let stateChanged = false;

    if (request.notes) {
      this.completedSections.notes = true;
      this.visibleSections.notes = true;
      stateChanged = true;
    }

    if (request.letterOfMotivation) {
      this.completedSections.lom = true;
      this.visibleSections.lom = true;
      stateChanged = true;
    }

    if (request.radiologyRequest) {
      this.completedSections.radiologyRequest = true;
      this.visibleSections.radiologyRequest = true;
      stateChanged = true;
    }

    if (request.letterToColleague) {
      this.completedSections.letterToColleague = true;
      this.visibleSections.letterToColleague = true;
      stateChanged = true;
    }

    if (request.letterToGP) {
      this.completedSections.letterToGP = true;
      this.visibleSections.letterToGP = true;
      stateChanged = true;
    }

    if (request.letterToPatient) {
      this.completedSections.letterToPatient = true;
      this.visibleSections.letterToPatient = true;
      stateChanged = true;
    }

    if (request.letterToPatientWork) {
      this.completedSections.letterToPatientWork = true;
      this.visibleSections.letterToPatientWork = true;
      stateChanged = true;
    }

    // E5 WITHDRAWN 2026-09-01. This used to set `visibleSections.riskManagement` whenever the
    // extractor had returned anything, which is why RISK MANAGEMENT was open on arrival while
    // TASK LIST — sitting on the same kind of passive suggestion list — was not.
    //
    // Every OTHER auto-reveal in this method fires on a GENERATED DOCUMENT: a letter, the
    // motivation, the radiology request. The user asked for those, so opening them is
    // finishing the job. Suggestions are passive output nobody requested, and the sidebar is
    // how they are opened.
    //
    // The TICK stays: confirmed items are genuinely completed work, and it survives a reload
    // because `completedSections` is persisted (:1021).
    if (this.riskItemsForThisAIRequest.length) {
      this.completedSections.riskManagement = true;
      stateChanged = true;
    }

    // Always update checklist items (will use user's preferred tests if available, otherwise AI suggestions)
    this.updateLabTestChecklistItems();

    
  }

  private setVisibleSection(elementId: string): void {
    switch (elementId) {
      case 'top':
        this.visibleSections.details = true;
        break;
      case 'notes':
        this.visibleSections.notes = true;
        break;
      case 'file_update':
        this.visibleSections.fileUpdate = true;
        break;
      case 'medication':
        this.visibleSections.medication = true;
        break;
      case 'labTests':
        this.visibleSections.labTests = true;
        break;
      case 'tasks':
        this.visibleSections.tasks = true;
        break;
      case 'riskManagement':
        this.visibleSections.riskManagement = true;
        break;
      case 'consent':
        this.visibleSections.consent = true;
        break;
      case 'lom':
        this.visibleSections.lom = true;
        break;
      case 'radiology_request':
        this.visibleSections.radiologyRequest = true;
        break;
      case 'letter_to_colleague':
        this.visibleSections.letterToColleague = true;
        break;
      case 'letter_to_gp':
        this.visibleSections.letterToGP = true;
        break;
      case 'letter_to_patient':
        this.visibleSections.letterToPatient = true;
        break;
      case 'letter_to_patient_employer':
        this.visibleSections.letterToPatientWork = true;
        break;
      default:
        break;
    }
    // State will be persisted in returnToParent or saveRequest
  }

  /**
   * Persists the current completedSections and visibleSections state to the backend
   */
  private persistSectionState(): void {
    if (!this.editingRequest?.id) {
      return;
    }

    const update = {
      completedSections: this.completedSections,
      visibleSections: this.visibleSections
    };

    this.aiService.updateAIRequest(this.editingRequest.id, update)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { },
        error: (err) => console.error('Failed to persist section state', err)
      });
  }

  // Central method to handle an incremental update.
  private handleIncrementalUpdate(update: any): void {
    // Merge the update into the overall editingRequest object.
    this.editingRequest = { ...this.editingRequest, ...update };


    // Special handling for "motivationCodingBundleId" update.
    if (update.motivationCodingBundleId) {
      if (!this.codingBundles || this.codingBundles.length === 0) {
        this.codingBundles = this.codingBundleService.codingBundlesSubject.getValue();
      }
      const selectedMotivationCodingBundle = this.codingBundles.find(
        (cb) => cb.id === update.motivationCodingBundleId
      );
      if (selectedMotivationCodingBundle) {

        // Update the local variable for motivation procedure codes
        this.selectedMotivationProcedureCodes = selectedMotivationCodingBundle.procedureCodes.map((c: any) => ({
          ...c,
          paidCash: selectedMotivationCodingBundle.paidCash,
          noCharge: selectedMotivationCodingBundle.noCharge,
        }));
        // Optionally, you might want to patch a form field if you're displaying these codes.
        /* TODO test if the selected codes are updating when this comes in */
        this.lomForm.patchValue({ motivationCodingBundleId: update.motivationCodingBundleId }, { emitEvent: true });
        // Answers the TODO above for the persistence half at least: the expanded bundle now
        // reaches the `motivationProcedureCodes` control, so `getAllDetails()` can see it (§1.1).
        this.syncMotivationProcedureCodesToForm();


      }
    }

    //Special handling for "codingBundleId" updated
    if (update.codingBundleId) {
      const selectedCodingBundle = this.codingBundles.find(cb => cb.id === update.codingBundleId);
      if (selectedCodingBundle) {
        this.selectedProcedureCodes = selectedCodingBundle?.procedureCodes.map((c: any) => { return { ...c, paidCash: selectedCodingBundle.paidCash, noCharge: selectedCodingBundle.noCharge } }) || [];
        this.notesForm.patchValue({ paidCash: selectedCodingBundle.paidCash, noCharge: selectedCodingBundle.noCharge });
      }
    }

    // Special handling for "allergies" update.
    if (update.allergies) {
      // Compute unique allergies: filter out those already in the patient's allergies.
      const uniqueAllergies = update.allergies.filter(
        (allergy: any) => !this.patient.allergies.some((patientAllergy: any) => patientAllergy.id === allergy.id)
      );
      // Update the form and local variable if the control is pristine.
      const currentAllergyControl = this.notesForm.get('allergies');
      if (currentAllergyControl && currentAllergyControl.pristine) {
        currentAllergyControl.patchValue(uniqueAllergies, { emitEvent: false });
      }
      this.allergies = uniqueAllergies;
    }

    // Create a copy of the update and remove keys that were handled specially.
    const generalUpdate = { ...update };
    delete generalUpdate.motivationCodingBundleId;
    delete generalUpdate.allergies;

    // Now, selectively patch each form that might be affected.
    this.patchFormWithUpdate(this.notesForm, generalUpdate);
    this.patchFormWithUpdate(this.lomForm, generalUpdate);
    this.patchFormWithUpdate(this.tasksForm, generalUpdate);
    // E2 — so a `suggestedRiskItems` update arriving while the request is open reaches the panel,
    // and `determineCompletedSections` below can then reveal it (E5).
    this.patchFormWithUpdate(this.riskForm, generalUpdate);
    this.patchFormWithUpdate(this.medicationForm, generalUpdate);
    this.patchFormWithUpdate(this.consentForm, generalUpdate);

    this.determineCompletedSections(generalUpdate);
  }

  // Helper method to patch a given form selectively.
  private patchFormWithUpdate(form: FormGroup, update: any): void {
    Object.keys(update).forEach(key => {
      if (form.contains(key)) {
        const control = form.get(key);
        // Only update if the control is pristine (unchanged by the user)
        if (control && control.pristine) {
          control.patchValue(update[key], { emitEvent: false });
        }
      }
    });
  }

  addDiagnosisField(value?: any): void {
    const nextIndex = this.visibleDiagnosisFields.length + 2; // Start at 2 for diagnosis2
    if (nextIndex <= 24) {
      const fieldName = `diagnosis${nextIndex}`;
      this.diagnosisForm.addControl(fieldName, this.fb.control(''));
      if (value) {
        this.diagnosisForm.patchValue({ [fieldName]: value })
      }
      this.visibleDiagnosisFields.push(fieldName);
    }
  }

  selectConsentType(event: any) {
    const consentType = { ...event.value };

    if (consentType.conditionalConsent) {
      consentType.conditionalConsent = [...consentType.conditionalConsent].map((c: any) => { return { consentGiven: true, name: c } }) || [];
    }
    if (consentType.informationSheets) {
      consentType.informationSheets = consentType.informationSheets.map((sheet: any) => { return { ...sheet, sendSheet: true } }) || []
    }

    consentType.selectedRequiredConsent = consentType.additionalRequiredConsent ? consentType.additionalRequiredConsent[0] : '';
    consentType.selectedProcedureSide = '';
    consentType.selectedImplant = '';
    consentType.selectedTransfusion = '';

    let detectedDetails;
    if (consentType.consentType === 'interactive') {
      const { detectedProcedureTitle, detectedCondition, detectedRisks } = this.consentForm.value;
      detectedDetails = { detectedProcedureTitle, detectedCondition, detectedRisks };
    }

    this.selectedConsentForm = {
      ...consentType,
      procedureTitle: detectedDetails?.detectedProcedureTitle || consentType.procedureTitle || '',
      conditionText: detectedDetails?.detectedCondition || '',
      specificRisksText: detectedDetails?.detectedRisks || '',
      email: this.patient.email
    };

    /*
     * THE TICKS DO NOT SURVIVE A CHANGE OF CONSENT FORM.
     *
     * `[selection]` is an input, and clearing it here is what lets the picker fall back to what
     * the practice configured for the newly chosen form. Carrying the previous form's selection
     * across would silently attach documents to a consent nobody chose them for.
     */
    this.selectedConsentDocumentIds = [];

    setTimeout(() => {
      this.consentForm.patchValue({ consentFormId: consentType.id });
      this.cd.detectChanges();
    });
  }

  compileConsentText(): string {

    let result = '';
    if (this.selectedConsentForm.consentType === 'standard') {
      result = this.selectedConsentForm.text;
    }

    if (this.selectedConsentForm.consentType === 'interactive') {
      result = this.consentFormsService.compileInteractiveConsentResult(this.selectedConsentForm, false);
    }

    return result;
  }

  sendConsent() {

    this.analyticsService.trackButtonClick('AI Request Send Consent');

    const date: moment.Moment = this.consentForm.value.date;

    const consentText = this.compileConsentText();

    const data = {
      ...this.consentForm.value,
      formDetails: this.selectedConsentForm,
      consentGiven: false,
      email: this.selectedConsentForm.email,
      date: date.utc().startOf('day').toISOString(),
      // Links the consent back to this AI request so the section can show it was already sent
      aiRequestId: this.editingRequest.id,
      consentText,
      // §29. The same field name `patient-consent-detail` posts, read by `createConsentInvite`:
      // documents that need a response become wizard steps on the invite, and information-only
      // ones ride this email as attachments.
      practiceDocumentIds: this.selectedConsentDocumentIds
    };


    const formData = new FormData();
    delete data.signature;
    formData.append('data', JSON.stringify(data));

    const getMostRecentConsentId = (consents: any[]) => {
      if (!consents?.length) return;
      const sorted = _.chain(consents)
        .orderBy(i => i.createdAt, 'desc')
        .value();
      return sorted[0].id;
    }

    this.consentService.addConsent(formData)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((consent) => this.inviteService.addInvite(data, true, [getMostRecentConsentId(consent.data)]))
      )
      .subscribe({
        next: (result) => {
          this.showToastMessage(result?.sentToApp ? 'Sent by email and to the patient app.' : 'Patient consent sent successfully');
          this.completedSections.consent = true;
          this.visibleSections.consent = true;
          // State will be persisted in returnToParent or saveRequest
        },
        error: (error) => {
          console.error('Error sending patient consent: ', error);
          this.showToastMessage('There was an error sending the patient consent. Please try again.');
        }
      });

  }

  private filterColleagues(value: string): Observable<any[]> {
    if (!value || typeof value !== 'string') return of([]);
    const filterValue = value.toLowerCase();
    return of(this.colleagues)
      .pipe(
        map(colleagues => colleagues.map(colleague => {
          colleague.fullName = `${colleague.title} ${colleague.name} ${colleague.surname}`;
          return colleague;
        }).filter(colleague =>
          colleague.fullName?.toLowerCase().includes(filterValue) ||
          colleague.email?.toLowerCase().includes(filterValue)
        ))
      );
  }

  selectColleague(colleague: any) {
    this.colleagueForm.patchValue({ colleagueName: `${colleague.title} ${colleague.name} ${colleague.surname}`, colleagueEmail: colleague.email, colleagueSpeciality: colleague.speciality });
  }


  private filterMedicalTeam(value: string): Observable<any[]> {
    if (!value || typeof value !== 'string') return of([]);
    const filterValue = value.toLowerCase();
    return of(this.medicalTeam)
      .pipe(
        map(members => members.map(member => {
          member.fullName = `${member.title} ${member.name} ${member.surname}`;
          return member;
        }).filter(colleague =>
          colleague.fullName.toLowerCase().includes(filterValue) ||
          colleague.email.toLowerCase().includes(filterValue)
        ))
      );
  }

  selectTeamMember(member: any) {
    this.gpForm.patchValue({ generalPractitioner: `${member.title} ${member.name} ${member.surname}`, generalPractitionerEmail: member.email, generalPractitionerPhone: member.phone });
  }



  hideAllergiesBanner() {
    this.hideAllergies = true;
  }

  resetForm() {
    setTimeout(() => {
      this.editingRequest = undefined;
      this.selectedConsentForm = undefined;
      this.selectedConsentDocumentIds = [];
      this.noteMediaObjects = [];
      this.selectedAllergies = [];
      this.selectedMedicalHistory = [];
      this.selectedMedicationHistory = [];
      this.visibleDiagnosisFields = [];
      this.notesForm.reset();
      this.tasksForm.reset();
      this.riskForm.reset();
      this.medicationForm.reset();
      this.reportForm.reset();
      this.lomForm.reset();
      this.radiologyForm.reset();
      this.colleagueForm.reset();
      this.gpForm.reset();
      this.patientForm.reset();
      this.patientWorkForm.reset();
      //this.dischargeForm.reset();
      this.initForms();
      this.cd.detectChanges();
    });
  }

  expandedIds: string[] = ['notes'];

  isExpanded(elementId: string) {
    return this.expandedIds.indexOf(elementId) !== -1;
  }

  scrollTo(elementId: string, scrollToContainerTop: boolean = false): void {
    this.setVisibleSection(elementId);
    if (this.expandedIds.indexOf(elementId) === -1) {
      this.expandedIds.push(elementId);
    }
    setTimeout(() => {
      this.scrollPositionService.scrollTo(elementId, scrollToContainerTop);
    }, 250);
  }

  private getContent(section: 'notes' | 'patientReport' | 'letterOfMotivation' | 'radiologyRequest' | 'letterToColleague' | 'letterToGP' | 'letterToPatient' | 'letterToPatientWork') {
    let content = '';
    let type = ''

    switch (section) {

      case 'notes':
        content = this.notesForm.get('notes')?.value;
        type = 'Note';
        break;

      case 'patientReport':
        content = this.reportForm.get('patientReport')?.value;
        type = 'Patient Report';
        break;
      case 'letterOfMotivation':
        content = this.lomForm.get('letterOfMotivation')?.value;
        type = 'Letter of Motivation';
        break;
      case 'radiologyRequest':
        content = this.radiologyForm.get('radiologyRequest')?.value;
        type = 'Request for Radiology';
        break;
      case 'letterToColleague':
        content = this.colleagueForm.get('letterToColleague')?.value;
        type = 'Letter to Colleague';
        break;
      case 'letterToGP':
        content = this.gpForm.get('letterToGP')?.value;
        type = this.selectedGroup?.groupSpeciality === 'General Practice' ? 'Letter to Specialist' : 'Letter to GP';
        break;
      case 'letterToPatient':
        content = this.patientForm.get('letterToPatient')?.value;
        type = 'Letter to Patient';
        break;
      case 'letterToPatientWork':
        content = this.patientWorkForm.get('letterToPatientWork')?.value;
        type = 'Letter to Patient Employer';
        break;
      /* case 'dischargeSummary':
        content = this.dischargeForm.get('dischargeSummary')?.value;
        type = 'Discharge Summary';
        break; */
    }

    return [content, type];
  }

  downloadAsPdf(section: 'notes' | 'patientReport' | 'letterOfMotivation' | 'radiologyRequest' | 'letterToColleague' | 'letterToGP' | 'letterToPatient' | 'letterToPatientWork') {
    this.analyticsService.trackButtonClick(`AI Request Download PDF ${section}`);
    const content = this.getContent(section);
    if (content[0].trim()) {
      const filename = `MedicAI_${this.patientName}_${content[1]}_${moment().valueOf()}`;
      let includeSignature = false;
      let selectedDoctor = undefined;
      let pdfDate: Date | undefined = undefined;
      switch (section) {
        case 'notes':
          includeSignature = this.notesForm.get('includeSignatureOnPdf')?.value;
          selectedDoctor = this.notesForm.get('selectedDoctor')?.value;
          pdfDate = this.notesForm.get('pdfDate')?.value;
          break;
        case 'patientReport':
          includeSignature = this.reportForm.get('includeSignatureOnPdf')?.value;
          selectedDoctor = this.reportForm.get('selectedDoctor')?.value;
          pdfDate = this.reportForm.get('pdfDate')?.value;
          break;
        case 'letterOfMotivation':
          includeSignature = this.lomForm.get('includeSignatureOnPdf')?.value;
          selectedDoctor = this.lomForm.get('selectedDoctor')?.value;
          pdfDate = this.lomForm.get('pdfDate')?.value;
          break;
        case 'radiologyRequest':
          includeSignature = this.radiologyForm.get('includeSignatureOnPdf')?.value;
          selectedDoctor = this.radiologyForm.get('selectedDoctor')?.value;
          pdfDate = this.radiologyForm.get('pdfDate')?.value;
          break;
        case 'letterToColleague':
          includeSignature = this.colleagueForm.get('includeSignatureOnPdf')?.value;
          selectedDoctor = this.colleagueForm.get('selectedDoctor')?.value;
          pdfDate = this.colleagueForm.get('pdfDate')?.value;
          break;
        case 'letterToGP':
          includeSignature = this.gpForm.get('includeSignatureOnPdf')?.value;
          selectedDoctor = this.gpForm.get('selectedDoctor')?.value;
          pdfDate = this.gpForm.get('pdfDate')?.value;
          break;
        case 'letterToPatient':
          includeSignature = this.patientForm.get('includeSignatureOnPdf')?.value;
          selectedDoctor = this.patientForm.get('selectedDoctor')?.value;
          pdfDate = this.patientForm.get('pdfDate')?.value;
          break;
        case 'letterToPatientWork':
          includeSignature = this.patientWorkForm.get('includeSignatureOnPdf')?.value;
          selectedDoctor = this.patientWorkForm.get('selectedDoctor')?.value;
          pdfDate = this.patientWorkForm.get('pdfDate')?.value;
          break;
        /*  case 'dischargeSummary':
           includeSignature = this.dischargeForm.get('includeSignatureOnPdf')?.value;
           selectedDoctor = this.dischargeForm.get('selectedDoctor')?.value;
           break; */

      }
      if (includeSignature && this.isPhysician) {

        const appendSignatureMessage = content[0];
        this.pdfDownloadService.downloadPdf({
          content: appendSignatureMessage,
          filename: filename,
          patient: this.patient,
          images: [],
          attachSignature: includeSignature,
          signatureUrl: this.userProfile.signatureImageUrl,
          signatoryName: `${this.userProfile.title} ${this.userProfile.name} ${this.userProfile.surname}`,
          date: pdfDate,
        });

      } else if (!this.isPhysician && this.hasSignaturePermission && includeSignature) {
        const appendSignatureMessage = content[0];
        this.pdfDownloadService.downloadPdf({
          content: appendSignatureMessage,
          filename: filename,
          patient: this.patient,
          images: [],
          attachSignature: includeSignature,
          signatureUrl: selectedDoctor?.signatureImageUrl,
          signatoryName: `${selectedDoctor?.title} ${selectedDoctor?.name} ${selectedDoctor?.surname}`,
          date: pdfDate,
        });

      } else if (!includeSignature || !this.canSign) {
        this.pdfDownloadService.downloadPdf({
          content: content[0],
          filename: filename,
          patient: this.patient,
          images: [],
          attachSignature: false,
          date: pdfDate,
        });

      }

      this.closeAllMenus(this.menuTriggers);

      // C2 — the download hook, mirroring the save-to-timeline one. Downloading the motivation
      // is the other moment the letter leaves this screen for the scheme; §7 names both.
      this.maybeOfferTheatreBooking(section);
    }
  }

  sendViaEmail(section: 'letterToColleague' | 'letterToGP' | 'letterToPatient' | 'letterToPatientWork') {
    this.analyticsService.trackButtonClick(`AI Request Send Via Email ${section}`);

    const { colleagueEmail, colleagueName, colleagueSpeciality, letterToColleague } = this.colleagueForm.value;
    const { generalPractitioner, generalPractitionerEmail, letterToGP } = this.gpForm.value;
    const { patientEmail, letterToPatient } = this.patientForm.value;
    const { patientEmployer, letterToPatientWork } = this.patientWorkForm.value;

    let email = '';
    let content = '';
    let name = '';
    let type = '';
    let speciality = ''
    let includeSignature = false;
    let selectedDoctor = undefined;
    let pdfDate: Date | undefined = undefined;
    switch (section) {
      case 'letterToColleague':
        type = 'Letter To Colleague';
        email = colleagueEmail;
        content = letterToColleague;
        name = colleagueName;
        speciality = colleagueSpeciality;
        includeSignature = this.colleagueForm.get('includeSignatureOnEmail')?.value;
        selectedDoctor = this.colleagueForm.get('selectedDoctor')?.value;
        pdfDate = this.colleagueForm.get('pdfDate')?.value;
        break;
      case 'letterToGP':
        type = this.selectedGroup?.groupSpeciality === 'General Practice' ? 'Letter to Specialist' : 'Letter to GP';
        email = generalPractitionerEmail;
        content = letterToGP;
        name = generalPractitioner;
        includeSignature = this.gpForm.get('includeSignatureOnEmail')?.value;
        selectedDoctor = this.gpForm.get('selectedDoctor')?.value;
        pdfDate = this.gpForm.get('pdfDate')?.value;
        break;
      case 'letterToPatient':
        type = 'Letter To Patient';
        email = patientEmail;
        content = letterToPatient;
        name = `${this.patient.name} ${this.patient.surname}`;
        includeSignature = this.patientForm.get('includeSignatureOnEmail')?.value;
        selectedDoctor = this.patientForm.get('selectedDoctor')?.value;
        pdfDate = this.patientForm.get('pdfDate')?.value;
        break;
      case 'letterToPatientWork':
        type = 'Letter To Patient Employer';
        email = patientEmail;
        content = letterToPatientWork;
        name = patientEmployer || `${this.patient.name} ${this.patient.surname}`;
        includeSignature = this.patientWorkForm.get('includeSignatureOnEmail')?.value;
        selectedDoctor = this.patientWorkForm.get('selectedDoctor')?.value;
        pdfDate = this.patientWorkForm.get('pdfDate')?.value;
        break;
    }

    if (!email) {
      email = this.selectedGroup.practiceEmail;
      this.showToastMessage('An email address was not provided. Sending to the practice email if available.');
    }

    if (!email) {
      this.showToastMessage('An email address was not provided. Please check the relevant email field try again.');
      return;
    }
    if (includeSignature && this.isPhysician) {
      this.emailCommunication(this.patient.id, type, name, email, content, speciality, true, this.userProfile.signatureImageUrl, `${this.userProfile.title} ${this.userProfile.name} ${this.userProfile.surname}`, pdfDate)

    } else if (!this.isPhysician && this.hasSignaturePermission && includeSignature) {
      this.emailCommunication(this.patient.id, type, name, email, content, speciality, true, selectedDoctor?.signatureImageUrl, `${selectedDoctor?.title} ${selectedDoctor?.name} ${selectedDoctor?.surname}`, pdfDate)

    } else if (!includeSignature || !this.canSign) {
      this.emailCommunication(this.patient.id, type, name, email, content, speciality, undefined, undefined, undefined, pdfDate)
    }

  }

  emailCommunication(patientId: string, type: string, name: string, email: string, content: any, speciality: string, attachSignature?: boolean, signatureUrl?: string, signatoryName?: string, pdfDate?: Date | string): void {
    const plainTextContent = this.pdfDownloadService.convertToPlainText(content);
    this.patientCommunicationService.sendAIContentEmail({
      patientId: patientId,
      type: type,
      name: name,
      email: email,
      content: plainTextContent,
      speciality: speciality,
      images: [],
      attachSignature: attachSignature,
      signatureUrl: signatureUrl,
      signatoryName: signatoryName,
      pdfDate: pdfDate,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Email sent successfully');
          this.closeAllMenus(this.menuTriggers);
        },
        error: (error) => {
          console.error('Error sending AI email: ', error);
          this.showToastMessage('There was an error sending the email. Please try again.');
        }
      });
  }

  public dialogRef!: MatDialogRef<any>
  async openAddNoteDialog(content: any, title?: string) {
    let { noteTypeId, suggestedNoteTitle, paidCash, noCharge, codingBundleId, suggestedProcedureCodes } = this.notesForm.value;
    let noteType;

    if (content !== 'Note') {
      noteType = this.noteTypes.filter(nt => nt.label === content)[0];
    } else {
      noteType = this.noteTypes.filter(nt => nt.id === noteTypeId)[0];
    }

    let procedureCodes = this.selectedProcedureCodes;

    // attach start and end times to codes if we are dealing with a physio practice
    if (this.selectedGroup?.groupSpeciality === 'Physiotherapy') {
      procedureCodes = procedureCodes.map(code => ({
        ...code,
        startTime: this.notesForm.get('startTime')?.value,
        endTime: this.notesForm.get('finishTime')?.value
      }));
    }


    if (title) {
      procedureCodes = [];
      paidCash = false;
      noCharge = false;
    }

    // G3 — §6.2. Resolve the theatre team BEFORE the dialog renders, so the assistant fields
    // open already carrying the right person rather than changing under the user.
    procedureCodes = await this.applyTheatreTeamPrefill(procedureCodes, !!noteType?.isProcedure);

    this.addingNote = {
      date: moment(this.editingRequest.createdAt),
      noteTypeId: noteType?.id || null,
      title: title || suggestedNoteTitle,
      isProcedure: noteType?.isProcedure || false,
      isBilling: noteType?.isBilling || false,
      procedureCodes: procedureCodes,
      paidCash,
      noCharge,
      providerId: this.selectedProviderId, // Add providerId to the note data
      codingBundleId: codingBundleId,
      suggestedProcedureCodes: suggestedProcedureCodes
    };
    // §3d — cleared before the dialog opens, then computed for what it opened with. A verdict
    // left over from the previous note would render against this one's codes.
    this.bookingComparison = null;

    this.dialogRef = this.dialog.open(this.addNoteDialog!/* , { disableClose: true } */);

    // After `open`, so the first render is never blocked on a round trip: the dialog appears
    // immediately and the warning arrives when it arrives. A user who has already fixed the
    // codes by then simply sees it resolve.
    this.refreshBookingComparison();

    return this.dialogRef.afterClosed().toPromise();
  }

  selectAddingNoteType(ev: MatSelectChange) {
    const noteTypeId = ev.value;
    const noteType = this.noteTypes.find(nt => nt.id === noteTypeId);
    if (noteType) {
      this.addingNote.isProcedure = noteType.isProcedure;
      // §3d — `isProcedure` is the gate on the whole comparison, and it can be flipped in the
      // dialog. Switching a consult note to a procedure note must start comparing; switching
      // back must stop, and clear a warning that no longer applies to anything.
      this.refreshBookingComparison();
    }
  }

  openAddTaskDialog(content: any): Promise<TaskDialogResult | null | undefined> {
    const data: TaskDialogData = content
      ? { date: moment(content.date), task: content.task, dueDate: content.dueDate || '', aiGenerated: true }
      : { aiGenerated: true };
    const ref = this.dialog.open<TaskDialogComponent, TaskDialogData, TaskDialogResult | null>(TaskDialogComponent, { width: '600px', data });
    // `findReplaceTargets` reads the open task's notes through `addingTask`, so it must be the dialog's own model.
    this.addingTask = ref.componentInstance.model;
    return ref.afterClosed().toPromise();
  }

  onCloseNoteDialog(result: any) {
    this.dialogRef.close(result);
  }


  noteMediaObjects: FileObject[] = [];
  onNoteMediaUpload(files: FileObject[]) {

    const newMediaObjects = files.map(file => ({
      ...file,
      ///url: URL.createObjectURL(file),
      type: file.type,
    }));

    // Check for duplicates and avoid appending unnecessarily
    const uniqueMediaObjects = newMediaObjects.filter(newObj =>
      !this.noteMediaObjects.some(existingObj => existingObj.url === newObj.url)
    );

    this.noteMediaObjects = [...this.noteMediaObjects, ...uniqueMediaObjects];
  }

  onFilesEdited(files: any) {
    this.noteMediaObjects = files as FileObject[];
  }

  onNoteMediaDelete(fileObject: FileObject) {
    const index = this.noteMediaObjects.findIndex(obj => obj.url === fileObject.url);
    if (index > -1) {
      this.noteMediaObjects.splice(index, 1);
    }
  }

  hasTimlineData(type: 'notes' | 'patientReport' | 'letterOfMotivation' | 'radiologyRequest'): boolean {
    const existing = this.patientNotesForThisAIRequest;
    if (!existing?.length) return false;

    switch (type) {
      case 'notes':
        return existing.filter(note => note.title !== 'Letter Of Motivation' && note.title !== 'Request for Radiology').length > 0
      case 'letterOfMotivation':
        return existing.filter(note => note.title === 'Letter Of Motivation').length > 0
      case 'radiologyRequest':
        return existing.filter(note => note.title === 'Request for Radiology').length > 0
      case 'patientReport':
        return existing.filter(note => note.title === 'Patient Report').length > 0
      default:
        return false;
    }
  }


  /**
   * Whether a lab order has already been placed from this AI request.
   *
   * Same idea as hasTimlineData() for notes: the order carries `aiRequestId`, so this survives
   * a reload and is true on any device, rather than reflecting what this screen happens to
   * have done since it was opened.
   */
  hasLabOrderForThisRequest(): boolean {
    return !!this.labOrderForThisAIRequest;
  }

  /** Whether a consent has already been sent to the patient from this AI request. */
  hasConsentForThisRequest(): boolean {
    return this.patientConsentsForThisAIRequest.length > 0;
  }

  async createPatientNote(section: 'notes' | 'patientReport' | 'letterOfMotivation' | 'radiologyRequest', noteTitle?: string) {
    this.analyticsService.trackButtonClick(`AI Request Add To Timeline ${section}`);
    const content = this.getContent(section);
    if (content[0].trim()) {


      const dialogResult = await this.openAddNoteDialog(content[1], noteTitle);

      if (!dialogResult) return;

      const { date, noteTypeId, title, isProcedure, isBilling, procedureCodes, providerId } = dialogResult;

      if (!date || !title) {
        this.showToastMessage('Date and Procedure/Title are required to add a note');
        return
      };

      if (this.hasTimlineData(section as any)) {
        const confirmed = await this.showConfirmationDialog('ENTRY ALREADY EXISTS', 'An entry in this patients timeline has already been generated using this AI request. Please remember to check the patients clinical timeline for any duplicates created by saving an additional entry.')
        if (!confirmed) {
          return;
        }
      }

      const formValues = this.notesForm.value;
      const newMediaFiles = this.noteMediaObjects.filter(obj => !obj.id);


      let noteData = {
        date: date.utc().startOf('day').toISOString(),
        groupId: formValues.groupId,
        patientId: formValues.patientId,
        aiRequestId: this.editingRequest.id,
        // Carry the recording that produced this AI request through to the note (null for older requests)
        recordingJobId: this.editingRequest.recordingJobId ?? null,
        noteType: noteTypeId,
        noteTypeHeadings: '',
        isProcedure: isProcedure,
        isBilling: isBilling || false,
        procedureCodes: procedureCodes || [],
        title: title,
        notes: content[0],
        existingMedia: [],
        section,
        providerId: providerId // Add providerId to the note data
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(noteData));

      const mediaAppendPromises = newMediaFiles.map((obj, index) => {
        return fetch(obj.url)
          .then(response => response.blob())
          .then(blob => {
            formData.append(`newMedia${index}`, blob, `${moment().unix()}.${obj.type.split('/')[1]}`);
          });
      });

      Promise.all(mediaAppendPromises)
        .then(() => {
          this.noteService.addNote(formData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (result) => {
                this.showToastMessage('Patient note saved successfully');
                this.markAppointmentAsNoteSaved();
                this.usage.flow('consult note', 'done');
                // After validating a note to the timeline, update the AI Request core fields
                this.updateAIRequestAfterNoteSave(section, content[0]);
                // C2 — a letter of motivation that carries procedure codes is a theatre case
                // waiting to be booked. Offers, once, and remembers the answer server-side.
                this.maybeOfferTheatreBooking(section);
                // C4 — the note is IN THE TIMELINE at this point. Everything below is advisory:
                // it writes `capturedProcedureCodeId` back and raises a dismissible banner. It
                // cannot fail the save, because the save has already succeeded.
                this.runCaptureAndDivergence(result, dialogResult);
              },
              error: (error) => {
                console.error('Error saving patient note: ', error);
                this.showToastMessage('There was an error saving the patient note. Please try again.');
              }
            });
        });
    }
  }

  private updateAIRequestAfterNoteSave(section: 'notes' | 'patientReport' | 'letterOfMotivation' | 'radiologyRequest', content: string) {
    try {
      const aiUpdate: any = {};

      // Only persist notes content to AI Request when saving the main notes section
      if (section === 'notes' && content?.trim()) {
        aiUpdate.notes = content;
      }

      const startTime = this.notesForm.get('startTime')?.value;
      const finishTime = this.notesForm.get('finishTime')?.value;
      const codingBundleId = this.notesForm.get('codingBundleId')?.value;

      if (startTime) aiUpdate.startTime = startTime;
      if (finishTime) aiUpdate.finishTime = finishTime;
      if (codingBundleId) aiUpdate.codingBundleId = codingBundleId;

      if (Object.keys(aiUpdate).length === 0) return;

      this.aiService.updateAIRequest(this.editingRequest.id, aiUpdate)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { },
          error: (err) => console.error('Failed to update AI Request after note save', err)
        });
    } catch (err) {
      console.error('Unexpected error updating AI Request after note save', err);
    }
  }

  private markAppointmentAsNoteSaved() {
    console.log('markAppointmentAsNoteSaved: ', this.editingRequest.createdAt);
    try {
      if (!this.patient?.id) {
        console.warn('Cannot mark appointment as completed: patientId is not available');
        return;
      }

      const aiRequestCreatedAt = this.editingRequest?.createdAt ? moment(this.editingRequest.createdAt).toISOString() : undefined;
      
      this.patientAppointmentService.markAppointmentsAsCompleted(this.patient.id, aiRequestCreatedAt)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            console.log('Appointments marked as completed:', result);
            // Send patient satisfaction surveys for the updated appointments
            if (result.data && Array.isArray(result.data)) {
              result.data.forEach((appointment: any) => {
                this.sendPatientSatisfactionSurvey(appointment);
              });
            }
          },
          error: (error) => {
            console.error('Failed to mark appointment as completed:', error);
          }
        });
    } catch (error) {
      console.error('Failed to mark appointment as completed:', error);
    }
  }

  private sendPatientSatisfactionSurvey(appointment: any) {
    if (this.stateUnit || !appointment.appointmentTypeId || (!this.patient.email && !this.patient.mobileNumber)) {
      return;
    }

    this.appointmentTypeSurveyTemplateService.getSurveyTemplatesForAppointmentType(appointment.appointmentTypeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const templates = response.data || [];
          const immediateTemplates = templates.filter((t: any) => t.surveyEnabled && t.surveyDelayDays === 0 && t.surveyDelayHours === 0);

          immediateTemplates.forEach((template: any) => {
            const sub: Subscription = this.surveyService.createSurvey({
              groupId: patientGroupId(this.patient, this.selectedGroup.id)!,
              patientId: this.patient.id,
              appointmentId: appointment.id,
              surveyTemplateId: template.id
            }).subscribe({
              next: res => { console.log(`Survey created for template: ${template.id}`, res); sub.unsubscribe(); },
              error: err => { console.error(`Error creating survey for template: ${template.id}`, err); sub.unsubscribe(); }
            });
          });
        },
        error: (error) => {
          console.error('Error fetching survey templates for appointment type:', error);
        }
      });
  }

  addAllergy(event: MouseEvent, allergy: any) {
    this.analyticsService.trackButtonClick(`AI Request Add Allergy`);
    event.preventDefault();
    event.stopPropagation();

    const currentAllergies = this.patient.allergies;
    currentAllergies.push({ allergy: allergy.allergy, reaction: allergy.reaction });

    this.patientService.updatePatient(this.patient.id, { id: this.patient.id, allergies: currentAllergies })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient allergies updated successfully');
        },
        error: (error) => {
          console.error('Error updating patient allergies: ', error);
          this.showToastMessage('There was an error updating the patient allergies. Please try again.');
        }
      });
  }


  addMedicalHistory(event: MouseEvent, history: any) {
    this.analyticsService.trackButtonClick(`AI Request Add Medical History`);
    event.preventDefault();
    event.stopPropagation();

    const current = this.patient.medicalHistory;
    current.push({ surgery: history.surgery, year: history.year });

    this.patientService.updatePatient(this.patient.id, { id: this.patient.id, medicalHistory: current })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient medical history updated successfully');
          this.completedSections.fileUpdate = true;
          this.visibleSections.fileUpdate = true;
        },
        error: (error) => {
          console.error('Error updating patient medical history: ', error);
          this.showToastMessage('There was an error updating the patient medical history. Please try again.');
        }
      });
  }

  addMedication(event: MouseEvent, medication: any) {
    this.analyticsService.trackButtonClick(`AI Request Add Medication`);
    event.preventDefault();
    event.stopPropagation();

    const current = this.patient.medicationHistory;
    current.push({ medication: medication.medication, dosage: medication.dosage });

    this.patientService.updatePatient(this.patient.id, { id: this.patient.id, medicationHistory: current })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient medication list updated successfully');
          this.completedSections.medication = true;
          this.visibleSections.medication = true;
        },
        error: (error) => {
          console.error('Error updating patient medication list: ', error);
          this.showToastMessage('There was an error updating the patient medication list. Please try again.');
        }
      });
  }

  //WILL ADD THE SIGNATURE INCLUDE ON PDF 
  async savePrescription() {
    const isValid = this.medicationAutocomplete?.isValid();
    if (!isValid) {
      this.showToastMessage('At least one medication item is required to create a prescription. Please try again.');
      return;
    }
    const saved = await this.medicationAutocomplete?.downloadPrescription();
    if (saved) {
      this.completedSections.medication = true;
      this.visibleSections.medication = true;
      this.showToastMessage('Prescription created successfully.');
      this.medicationAutocomplete?.resetState();
    }
  }

  //WILL ADD THE SIGNATURE INCLUDE ON EMAIL 
  async emailPrescription() {
    const isValid = this.medicationAutocomplete?.isValid();
    if (!isValid) {
      this.showToastMessage('At least one medication item is required to create a prescription. Please try again.');
      return;
    }
    const { email, ccEmail, includeSignatureOnEmail } = this.medicationForm.value
    const sent = await this.medicationAutocomplete?.emailPrescription(email, ccEmail, true, includeSignatureOnEmail);
    if (sent) {
      this.completedSections.medication = true;
      this.visibleSections.medication = true;
      this.showToastMessage('Prescription sent successfully.');
      this.medicationAutocomplete?.resetState();
    }
  }


  async addTask(event: MouseEvent, taskValue: any) {
    this.analyticsService.trackButtonClick(`AI Request Add Task`);
    event.preventDefault();
    event.stopPropagation();

    taskValue = taskValue?.task || '';

    const dialogResult = await this.openAddTaskDialog({ date: moment(), task: taskValue })

    if (!dialogResult) return;

    const { date, task, notes, dueDate, assigneeUserIds } = dialogResult;

    if (!date || !task) {
      this.showToastMessage('Date and Task are required to add a task');
      return
    };

    const taskData = {
      patientId: this.patient.id,
      groupId: this.patient.groupId,
      date: moment.utc(date).startOf('day').toISOString(),
      dueDate: moment.utc(dueDate).startOf('day').toISOString(),
      task,
      notes,
      assigneeUserIds,
      status: 'Pending'
    }

    this.taskService.addTask(taskData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient task saved successfully');
          this.completedSections.tasks = true;
          this.visibleSections.tasks = true;
        },
        error: (error) => {
          console.error('Error saving patient task: ', error);
          this.showToastMessage('There was an error saving the patient task. Please try again.');
        }
      });
  }

  onSelectedProcedureCodesChanged(codes: ProcedureCode[], addingNote: any) {
    if (addingNote) {
      addingNote.procedureCodes = codes;
      // §3d — the warning is only useful while it still describes what is on screen. The
      // selector emits on discrete add/remove actions rather than on keystrokes, so a
      // re-compare per emit is a handful of reads over the life of a dialog.
      this.refreshBookingComparison();
    } else {
      this.selectedProcedureCodes = codes;
    }
  }

  onSelectedMotivationProcedureCodesChanged(codes: ProcedureCode[], addingNote: any) {
    this.selectedMotivationProcedureCodes = codes;
    this.syncMotivationProcedureCodesToForm();
  }

  /**
   * Mirrors `selectedMotivationProcedureCodes` onto the `lomForm` control of the same name.
   *
   * `app-procedure-code-selector` is not a ControlValueAccessor, so the selection cannot be bound
   * with `formControlName` — it arrives as an `(codesChanged)` event into a plain class field.
   * A plain class field is invisible to `getAllDetails()`, which spreads `lomForm.value`, so the
   * codes the surgeon chose were POSTed to the letter generator and then dropped on the floor
   * (tasks/theatre-booking-and-procedure-lifecycle.md §1.1, lessons.md §5b).
   *
   * `emitEvent: false` because nothing subscribes to this control and the LOM form's valueChanges
   * feeds the autosave/dirty machinery; mirroring a value that is already on screen should not
   * look like a fresh user edit.
   */
  private syncMotivationProcedureCodesToForm() {
    this.lomForm?.patchValue(
      { motivationProcedureCodes: this.selectedMotivationProcedureCodes ?? [] },
      { emitEvent: false }
    );
  }

  /**
   * Restores the codes that were actually on this request's letter of motivation, if the server
   * has any, and mirrors whatever we end up with onto the form.
   *
   * `motivationProcedureCodes` (what the surgeon motivated for, persisted by
   * `generateAILetterOfMotivation`) WINS over a re-expansion of `motivationCodingBundleId`,
   * because a bundle id cannot express "the bundle, minus 1234, plus 5678" and that delta is
   * exactly what the scheme authorised. `suggestedMotivationProcedureCodes` is not consulted
   * here at all — it is the AI's read-only suggestion list and stays exactly as it is.
   *
   * Called from both the coding-bundle subscription and the request patch, because those two
   * can complete in either order.
   */
  private restoreMotivationProcedureCodes() {
    const stored = this.editingRequest?.motivationProcedureCodes;
    if (Array.isArray(stored) && stored.length > 0) {
      this.selectedMotivationProcedureCodes = stored;
    }
    this.syncMotivationProcedureCodesToForm();
  }

  // ═══ C2 — prompt to book the theatre case ═══════════════════════════════════════════
  //
  // tasks/theatre-booking-and-procedure-lifecycle.md §7. After the letter of motivation is
  // saved to the timeline or downloaded, offer to turn the codes it carries into a booking.
  //
  // THE GATE IS THE CODES, NOT A NEW FLAG. §7: "Gate on `section === 'letterOfMotivation'`
  // and the motivation actually carrying procedure codes — that gives theatre/procedure
  // motivations only with no new flag." A motivation for a scan or a chronic medication
  // carries no CPT codes and is never asked about.

  /**
   * Guards against a second dialog while the first is still open. Two rapid clicks on
   * "Add to timeline" would otherwise stack two identical prompts.
   *
   * This is re-entrancy only. It is NOT how a decline is remembered — that lives in the
   * database, because a class field is gone on the next reload and §7 asks for a decision
   * that sticks.
   */
  private theatreBookingOfferInFlight = false;

  /**
   * The codes that would go onto the booking.
   *
   * `editingRequest.motivationProcedureCodes` is preferred because it is what the SERVER holds
   * — written by `generateAILetterOfMotivation` at the one moment "these are the codes on this
   * letter" is unambiguously true, and it is the list the server will actually copy. The
   * on-screen selection is the fallback for the window between an edit and the next generate.
   */
  private motivationCodesForBooking(): any[] {
    const hasCode = (entry: any) => !!entry && typeof entry.code === 'string' && entry.code.trim().length > 0;
    const stored = this.editingRequest?.motivationProcedureCodes;
    if (Array.isArray(stored) && stored.some(hasCode)) {
      return stored.filter(hasCode);
    }
    return (this.selectedMotivationProcedureCodes || []).filter(hasCode);
  }

  /**
   * Offers "Book this theatre case?" after a letter of motivation reaches the timeline or the
   * user's downloads folder.
   *
   * Ordering matters and is deliberate:
   *
   *   1. section gate — letters to colleagues and radiology requests never ask;
   *   2. codes gate — a motivation with no procedure codes is not a theatre case;
   *   3. **ASK THE SERVER whether this offer has already been answered.** Accepting creates
   *      live rows; declining creates the same rows inactive. Either way this AI request is
   *      answered and is never asked about again — on this device or any other, this session
   *      or any later one. A `declined` boolean on the component would survive until the next
   *      navigation and no further, which is not what §7 asks for.
   *
   * On accept the rows are created FIRST and the user is routed to the booking flow only once
   * the server has confirmed them. Navigating first would leave the appointment screen offering
   * a motivation that does not exist yet.
   */
  private async maybeOfferTheatreBooking(section: string) {
    // A state unit plans no procedure codes.
    if (section !== 'letterOfMotivation' || this.stateUnit) return;
    if (this.theatreBookingOfferInFlight) return;

    const aiRequestId = this.editingRequest?.id;
    if (!aiRequestId) return;

    const codes = this.motivationCodesForBooking();
    if (codes.length === 0) return;

    this.theatreBookingOfferInFlight = true;
    try {
      const answered = await this.plannedProcedureCodeService.hasAnsweredOffer(aiRequestId)
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      // A failed lookup returns undefined. Staying silent is the right failure mode: a prompt
      // shown because the "already answered" check errored is exactly the re-prompt §7 forbids.
      if (!answered || answered.answered !== false) return;

      // Codes only. The descriptions are tariff text, not prose: a real four-code neurosurgical
      // motivation produced a 640-character confirmation dialog, one line of which was
      // "Craniotomy for Osteoplastic Flap for removal of: Meningioma, basal extracerebral mass,
      // intra ventricular tumours, pineal tumours, pituitary adenoma, total excision
      // cranio-pharyngioma/pharyngioma". Nobody reads that in a yes/no prompt, and a dialog people
      // dismiss without reading is worse than a shorter one they do read. The surgeon recognises
      // their own codes, and the full list with descriptions is on the booking screen behind it.
      const summary = codes.map((c: any) => c?.code).filter(Boolean).join(', ');

      const accepted = await this.showConfirmationDialog(
        'BOOK THIS THEATRE CASE?',
        `This letter of motivation carries ${codes.length} procedure code${codes.length === 1 ? '' : 's'} `
        + `(${summary}). Carry them onto a theatre booking for this patient?`,
        undefined,
        'Not now',
        'Book this case'
      );

      const request$ = accepted
        ? this.plannedProcedureCodeService.createFromAiRequest(aiRequestId)
        : this.plannedProcedureCodeService.dismissOffer(aiRequestId);

      request$.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          if (!accepted) return;
          this.showToastMessage('Procedure codes carried over — book the theatre case below');
          this.router.navigate(['/patients', { outlets: { patient: ['add-appointment'] } }]);
        },
        error: (error) => {
          console.error('Failed to record the theatre booking decision', error);
          // Only the accept path is worth interrupting the user for. A failed decline simply
          // means the offer is made again next time, which is a nuisance rather than a loss.
          if (accepted) {
            this.showToastMessage('Could not carry the procedure codes over. Please try again.');
          }
        }
      });
    } catch (error) {
      console.error('Unexpected error offering the theatre booking', error);
    } finally {
      this.theatreBookingOfferInFlight = false;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════════════
  //  G3 — the theatre team at note save (§6.2)
  // ═══════════════════════════════════════════════════════════════════════════════════

  /**
   * The server's verdict for this AI request, or null when nothing was resolved.
   *
   * THIS COMPONENT DOES NO MATCHING. It holds no roster, compares no names and never derives a
   * practice number. `utils/theatreTeamMatcher.js` decides, on the server, and the values below
   * arrive finished — §6.1: the model is never shown the roster, and the browser is not allowed
   * to become the place that quietly re-introduces the substitution the prompts guard against.
   */
  theatreTeamPrefill: TheatreTeamPrefillResponse | null = null;

  /** Guards a second prefill fetch while one is in flight. */
  private theatreTeamPrefillInFlight = false;

  /**
   * Writes the resolved surgical assistant onto the codes that carry one.
   *
   * Only `0008`/`0009` in a discipline with assistant rules take an assistant —
   * `CodePromptingService.shouldShowAssistantFields` already owns that rule and is reused here
   * rather than restated. If the note carries no such code the marker still shows, because the
   * detection is worth telling the user about even when there is nowhere to put it yet.
   *
   * Two rules the branches below encode:
   *
   *   - **A blank verdict writes nothing.** `keep-booking` with no booking gives `name === ''`,
   *     and blanking an assistant the user typed in the coding panel would be a silent loss.
   *   - **The booking never overwrites a person the user already named**; the TRANSCRIPT does,
   *     because that is what §6.2 asks for — visibly, via `marker`.
   */
  private async applyTheatreTeamPrefill(procedureCodes: any[], isProcedureNote: boolean): Promise<any[]> {
    this.theatreTeamPrefill = null;

    const codes = Array.isArray(procedureCodes) ? procedureCodes : [];
    const aiRequestId = this.editingRequest?.id;
    // The same gate the pipeline used to extract in the first place: procedure notes only.
    if (!isProcedureNote || !aiRequestId || this.theatreTeamPrefillInFlight) return codes;

    this.theatreTeamPrefillInFlight = true;
    try {
      const prefill = await this.theatreTeamService.loadNotePrefill(aiRequestId)
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      if (!prefill || prefill.success !== true) return codes;
      this.theatreTeamPrefill = prefill;

      const assistant = prefill.assistant;
      if (!assistant || !assistant.name) return codes;

      const disciplineCode = this.disciplineConfigService
        .getDisciplineCodeForSpeciality(this.selectedGroup?.groupSpeciality);

      return codes.map(code => {
        if (!code || !this.codePromptingService.shouldShowAssistantFields(String(code.code), disciplineCode)) {
          return code;
        }
        const alreadyNamed = !!(code.assistantName && String(code.assistantName).trim());
        if (alreadyNamed && assistant.source !== 'transcript') return code;
        return {
          ...code,
          assistantName: assistant.name,
          // From the matched Colleague or EMPTY. Never from the transcript, and never from a
          // different person's booking — an empty flagged number is an incomplete claim the
          // user can see and fix.
          assistantPracticeNumber: assistant.practiceNumber || ''
        };
      });
    } catch (error) {
      // A failed lookup leaves the dialog exactly as it would have been without this feature.
      console.error('Failed to resolve the theatre team for this note', error);
      return codes;
    } finally {
      this.theatreTeamPrefillInFlight = false;
    }
  }

  /** Whether the note dialog has anything to say about the theatre team. */
  get hasTheatreTeamMarker(): boolean {
    const prefill = this.theatreTeamPrefill;
    if (!prefill) return false;
    return !!prefill.assistant?.marker || !!prefill.anaesthetist?.marker
      || !!prefill.assistant?.practiceNumberFlagged;
  }

  /**
   * §6.2 row 3's offer: the dictation named somebody who is not on the roster.
   *
   * Creates the colleague with the roster flag set and NOTHING ELSE inferred — in particular no
   * practice number, because there is nowhere honest to get one from. The user is told where to
   * add it. `ColleagueService.addColleague` supplies the group.
   */
  addDetectedAssistantToRoster() {
    const assistant = this.theatreTeamPrefill?.assistant;
    const spoken = (assistant?.nameAsSpoken || '').trim();
    if (!spoken) return;

    this.analyticsService.trackButtonClick('AI Request Add Detected Assistant To Roster');

    const parts = spoken.split(/\s+/);
    const surname = parts.length > 1 ? parts[parts.length - 1] : spoken;
    const name = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';

    this.colleagueService.addColleague({
      title: '',
      name,
      surname,
      speciality: '',
      email: null,
      contactNumber: '',
      // The practice number is deliberately absent. Nobody dictates a BHF number.
      practiceNumber: '',
      isSurgicalAssistant: true,
      isAnaesthetist: false,
      active: true
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        if (assistant) assistant.offerAddToRoster = false;
        this.showToastMessage(
          `${spoken} added to Surgical Assistants — add their practice number in Settings to complete the claim`);
      },
      error: (error) => {
        console.error('Failed to add the detected assistant to the roster', error);
        this.showToastMessage('Could not add this person to Surgical Assistants. Please try again.');
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════════════
  //  C4 — capture + the two-direction divergence banner (§7)
  // ═══════════════════════════════════════════════════════════════════════════════════

  /**
   * The last divergence answer, or null. Rendered as a DISMISSIBLE banner.
   *
   * It is set only after the note has already been written to the timeline, so there is no
   * code path by which it can prevent a save. §7: "Warn — never block. Explicitly."
   */
  divergence: CaptureResponse | null = null;

  dismissDivergenceBanner() {
    this.divergence = null;
  }

  // ═══════════════════════════════════════════════════════════════════════════════════
  //  §3d — the SAME comparison, BEFORE the save, inside the note dialog
  // ═══════════════════════════════════════════════════════════════════════════════════
  //
  // C4's banner is correct and stays. Its problem is timing: it appears once the note is in the
  // timeline, so a surgeon who sees "you planned 0163 and did not capture it" has to file a
  // correction rather than fix a chip. This runs the identical server-side comparison while the
  // dialog is still open and the codes are still editable.
  //
  // §3d also decides WHAT it compares against: the BOOKING, not the letter of motivation. The
  // codes on the theatre appointment are the most recent statement of intent — a surgeon who
  // edited them while booking meant to. The server's `resolvePlannedSet` encodes that order,
  // and both this and the post-save banner go through it, so the two can never disagree.

  /** The pre-save comparison, or null when there is nothing to compare or nothing to say. */
  bookingComparison: CompareResponse | null = null;

  /** Blocks overlapping compares when chips change faster than the round trip. */
  private bookingComparisonInFlight = false;

  /**
   * True when the booking was matched BY DATE rather than by a link the user made (§3d
   * workflow 3 — a case booked by hand with no letter). Rendered as part of the warning,
   * because a user who can see how we got to a booking can tell us we got the wrong one; one
   * who cannot just learns to dismiss the banner.
   */
  get bookingComparisonIsInferred(): boolean {
    return !!this.bookingComparison?.inferred;
  }

  /**
   * Runs the comparison for whatever is currently in the dialog.
   *
   * Gated on `isProcedure`: a consultation note has no theatre booking to disagree with, and a
   * comparison there would be a warning about a category error.
   *
   * Silent on failure, and `bookingComparison` is CLEARED rather than left stale — a warning
   * describing codes the user has since changed is worse than no warning, and this is advisory
   * either way. Nothing here can stop or delay the save.
   */
  private refreshBookingComparison(): void {
    // A state unit records no procedure codes, so there is nothing to compare.
    if (!this.addingNote?.isProcedure || this.stateUnit) {
      this.bookingComparison = null;
      return;
    }

    const aiRequestId = this.editingRequest?.id;
    const patientId = this.patient?.id;
    if (!aiRequestId && !patientId) return;
    if (this.bookingComparisonInFlight) return;

    const capturedCodes = (this.addingNote?.procedureCodes || [])
      .filter((entry: any) => entry && entry.code !== null && entry.code !== undefined)
      .map((entry: any) => ({
        code: String(entry.code),
        description: entry.description ?? null,
        // Sent so the server can classify it. A diff that treated modifiers as unplanned work
        // would fire on every note and be dismissed unread inside a week (§7).
        type: entry.type
      }));

    this.bookingComparisonInFlight = true;
    this.plannedProcedureCodeService.compareForNote({
      aiRequestId,
      patientId,
      // The NOTE's date anchors the date-window match, not today's. A note written up two days
      // later must still find the booking it is about.
      noteDate: this.addingNote?.date ? moment(this.addingNote.date).toISOString() : undefined,
      capturedCodes
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.bookingComparisonInFlight = false;
        this.bookingComparison = result && result.hasDivergence ? result : null;
        this.cd.detectChanges();
      },
      error: (error) => {
        this.bookingComparisonInFlight = false;
        this.bookingComparison = null;
        console.error('Could not compare the note codes against the theatre booking', error);
      }
    });
  }

  /**
   * Sends the codes the save actually produced to the server, which writes
   * `capturedProcedureCodeId` back onto the matching planned rows and answers with the
   * divergence in both directions.
   *
   * The ids come from the SAVED NOTE rather than from the dialog: the dialog's chips have no
   * `PatientProcedureCode` id until the note exists, and an id is what makes the write-back a
   * real foreign key instead of a code string that looks like one.
   *
   * Modifiers are sent along with everything else and classified server-side — a diff that
   * dropped them would report every modifier as unplanned and the banner would be dismissed
   * unread inside a week (§7).
   */
  private runCaptureAndDivergence(saved: any, dialogResult: any) {
    try {
      if (!dialogResult?.isProcedure || this.stateUnit) return;

      const aiRequestId = this.editingRequest?.id;
      if (!aiRequestId) return;

      const note = saved?.data;
      const source = Array.isArray(note?.procedureCodes) && note.procedureCodes.length
        ? note.procedureCodes
        : (dialogResult?.procedureCodes || []);

      const capturedCodes = (source || [])
        .filter((entry: any) => entry && (entry.code !== null && entry.code !== undefined))
        .map((entry: any) => ({
          id: typeof entry.id === 'string' ? entry.id : undefined,
          code: String(entry.code),
          description: entry.description ?? null,
          type: entry.type
        }));

      // Not gated on `capturedCodes.length`: a note that captured NOTHING is exactly the
      // planned-not-captured case — "the surgeon did less than planned, or a line was
      // forgotten" — and it is the direction nothing catches today.
      this.plannedProcedureCodeService.captureFromNote({
        patientNoteId: note?.id,
        aiRequestId,
        patientId: this.patient?.id,
        // §3d — the SAME anchor the pre-save comparison used. Sending a different one (or
        // none, and letting the server fall back to the AI request's createdAt) would let the
        // dialog and the banner resolve to different bookings for the same note.
        noteDate: dialogResult?.date ? moment(dialogResult.date).toISOString() : undefined,
        capturedCodes
      }).pipe(takeUntil(this.destroy$)).subscribe({
        next: (result) => {
          this.divergence = result && result.hasDivergence ? result : null;
        },
        error: (error) => {
          // Advisory only. The note is already saved and the user has already been told so;
          // a failed comparison must not read as a failed save.
          console.error('Failed to compare the captured codes against the booking', error);
        }
      });
    } catch (error) {
      console.error('Unexpected error running the coding divergence check', error);
    }
  }

  addProcedureCode(event: MouseEvent, code: any) {
    this.analyticsService.trackButtonClick(`AI Request Add Procedure Code`);
    event.preventDefault();
    event.stopPropagation();
    // Use the reusable component's addChip method
    this.procedureCodeSelector.addChip(code).then(() => {
      // After the chip is added, trigger change detection if needed
      this.cd.detectChanges();
    });
  }

  addMotivationProcedureCode(event: MouseEvent, code: any) {
    this.analyticsService.trackButtonClick(`AI Request Add Motivation Procedure Code`);
    event.preventDefault();
    event.stopPropagation();
    // Use the reusable component's addChip method
    this.motivationProcedureCodeSelector.addChip(code).then(() => {
      // After the chip is added, trigger change detection if needed
      this.cd.detectChanges();
    });
  }

  canAddTask(task: string) {
    return this.tasks.filter((t: any) => t.task.toLowerCase() === task.toLowerCase()).length === 0;
  }

  // ══ RISK MANAGEMENT — workstream E of tasks/risk-management.md ═══════════════════════════
  //
  // A doctor dictating a note routinely says things that are not clinical facts about the patient
  // but FACILITY FAILURES ("there was no 3-0 vicryl", "the dressing trolley on 3B was empty
  // again"). The extractor pulls them out; this section is where the doctor confirms one, at
  // which point it becomes a `PatientRiskItem` on the practice's register (§1).
  //
  // It is CLINICAL GOVERNANCE CORRESPONDENCE, NOT A CLINICAL RECORD, and that framing drives
  // everything below: nothing reaches the register without a click, the verbatim quote is on
  // screen before the click, and the facility a row will name is shown before the click too.

  /** E2/E5 — the extractor's suggestions for this request. `[]` for the null column, which is normal. */
  get riskSuggestions(): RiskSuggestion[] {
    return normaliseRiskSuggestions(this.riskForm?.get('suggestedRiskItems')?.value);
  }

  /**
   * The register items that came from THIS request. Used for `completedSections` and for the
   * "already added" list in the panel — the patient may well have items from other encounters,
   * and those belong in the register and the timeline, not in this request's section.
   */
  get riskItemsForThisAIRequest(): any[] {
    const requestId = this.editingRequest?.id;
    if (!requestId) return [];
    return (this.riskItems || []).filter((item: any) => item && item.aiRequestId === requestId);
  }

  /**
   * E4 — the dedupe feed, mirroring `canAddTask`.
   *
   * Scoped to the WHOLE patient, not to this request: the same stock-out dictated into a ward
   * round and then again into the procedure note is one facility issue, and two register rows
   * would overstate it on a document handed to a hospital.
   */
  get addedRiskIssues(): string[] {
    return (this.riskItems || []).map((item: any) => item?.issue).filter((issue: any) => !!issue);
  }

  /** E4, as a method, so the guard reads the same way `canAddTask` does at its call sites. */
  canAddRiskItem(issue: string): boolean {
    if (!issue) return false;
    const target = String(issue).trim().toLowerCase();
    return (this.riskItems || []).filter((item: any) => String(item?.issue || '').trim().toLowerCase() === target).length === 0;
  }

  /** E5 — suggestions to act on, or items already added. Nothing at all means no section. */
  get hasRiskManagementContent(): boolean {
    return this.riskSuggestions.length > 0 || this.riskItemsForThisAIRequest.length > 0;
  }

  /**
   * The calendar day AT THE FACILITY (§5c `dateOfIssue`) — the day of the RECORDING, not today.
   *
   * A request opened and confirmed the next morning must still file under the day the issue
   * happened, or J3's timeline grouping and the register's date filter both land on the wrong day.
   */
  private get riskDateOfIssue(): moment.Moment {
    return this.editingRequest?.createdAt ? moment(this.editingRequest.createdAt) : moment();
  }

  /**
   * E3 — CONFIRM BEFORE ADD.
   *
   * Opens the shared dialog with the suggestion, the verbatim quote (read-only, D8) and a preview
   * of the facility this row will name; on confirm, POSTs a `PatientRiskItem`.
   *
   * ── WHAT IS AND IS NOT SENT ──────────────────────────────────────────────────────────
   *
   * `hospitalId` / `hospitalName` / `wardName` / `location` are NOT in the payload. §4 resolves
   * them SERVER-SIDE at create time and `addRiskItem` discards the client's versions; sending them
   * would be sending a facility name that a hospital later reads off a letterhead. `appointmentId`
   * IS sent — it is not a column, it names the encounter so the resolver can run branch 2.
   *
   * ── WHY THERE IS A RECONCILIATION STEP ───────────────────────────────────────────────
   *
   * The dialog can only PREVIEW the facility: branch 3 reads `PatientAdmissionHistory` and matches
   * a hospital name back to a `Hospitals` row, which the client cannot do. So the preview is
   * marked provisional when the client lands on branch 4, and the toast after the POST always
   * names what the server actually resolved — the doctor is never left believing a row says
   * something it does not.
   */
  async addRiskItem(event: MouseEvent, suggestion: RiskSuggestion | null) {
    this.analyticsService.trackButtonClick('AI Request Add Risk Item');
    event?.preventDefault();
    event?.stopPropagation();

    if (this.addingRiskItem) return;

    // `groupId` is NOT NULL on the row and the POST 400s without it. This is an async method, so
    // an unguarded deref would surface as an unhandled rejection rather than an error the doctor
    // can act on.
    //
    // ⚠ THE `id` HALF IS NOW THE ENFORCEMENT POINT FOR `patientId`. User decision 2026-09-01:
    // *"patientId should never be null (enforced by the workflows and the UI, not needed on the db
    // layer)."* The column is still nullable and the server still accepts NULL, so nothing
    // downstream will catch a risk item posted without a patient — this early return is what makes
    // it impossible from here. `this.patient` comes from `currentPatient$`, so the patient is
    // genuinely in context and is never asked for.
    if (!this.patient?.id || !this.patient?.groupId) {
      this.showToastMessage('The patient is still loading. Please try again in a moment.');
      return;
    }

    if (suggestion?.issue && !this.canAddRiskItem(suggestion.issue)) {
      this.showToastMessage('That issue is already on the risk register for this patient.');
      return;
    }

    const dateOfIssue = this.riskDateOfIssue;

    // E3-1 — the appointment is chosen BEFORE the dialog as well as after, because §4 branch 2
    // runs server-side off the `appointmentId` in the body and nothing else. Previewing a branch
    // keyed on a different appointment than the one being sent would be previewing a question
    // nobody is going to ask.
    const previewEncounter = pickEncounterAppointment(this.appointments, dateOfIssue, this.patient);

    const dialogData: RiskItemAddDialogData = {
      suggestion,
      dateOfIssue,
      facility: predictRiskFacility({
        patient: this.patient,
        hospitals: this.hospitals,
        appointments: this.appointments,
        // E3-1 — branch 3. Without these the preview can only ever say "in rooms, provisionally".
        admissionRecords: this.admissionRecords,
        appointmentId: previewEncounter?.id ?? null,
        dateOfIssue,
        // The PATIENT's group, not the selected one — the POST files under `patient.groupId`, and
        // the branch-3 name match is scoped by group as a tenancy boundary (D5).
        groupId: this.patient.groupId,
      }),
      patientName: this.patient ? `${this.patient.name || ''} ${this.patient.surname || ''}`.trim() : null,
      // The doctor may correct the resolved facility. Same list `admit-details` uses, wards
      // nested; may be `null` if the fetch failed, and the dialog tolerates that.
      hospitals: this.hospitals,
    };

    const result: RiskItemAddDialogResult | null | undefined = await this.dialog
      .open(RiskItemAddDialogComponent, { data: dialogData, autoFocus: false })
      .afterClosed()
      .toPromise();

    if (!result) return;

    const encounter = pickEncounterAppointment(this.appointments, result.dateOfIssue, this.patient);

    // The SECOND `predictRiskFacility` call that used to sit here is gone. It re-ran the §4
    // preview against the final date so the post-save toast could report a client/server
    // disagreement — but the server now stores the facility this screen sends, so there is no
    // second opinion left to disagree with and nothing to reconcile.

    const riskItemData = {
      groupId: this.patient.groupId,
      patientId: this.patient.id,
      aiRequestId: this.editingRequest?.id ?? null,
      category: result.category,
      severity: result.severity,
      // DATEONLY — a calendar day at the facility, not an instant (§5c).
      dateOfIssue: moment(result.dateOfIssue).format('YYYY-MM-DD'),
      issue: result.issue,
      // Verbatim, exactly as extracted. Write-once server-side (D8).
      quote: result.quote,
      notes: result.notes,
      status: 'Open',
      // Consumed by the resolver for §4 branch 2; not a column.
      appointmentId: encounter?.id ?? null,
      // The doctor's explicit facility override, or NULL when they left the preview alone — in
      // which case the server resolves §4 exactly as it always has. Validated server-side against
      // this same `groupId`; an invalid choice is a 400, never a silent re-resolve.
      facilityChoice: result.facilityChoice,
      // §28 / D15 — the photographs attached in the dialog. MUST be forwarded: this object is
      // built field by field, so omitting the key would discard every photo the doctor attached
      // while still reporting a successful save. `addRiskItem` turns a non-empty array into a
      // multipart request and leaves an empty one as the JSON it has always been.
      media: result.media,
    };

    this.addingRiskItem = true;

    this.patientRiskItemService.addRiskItem(riskItemData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.addingRiskItem = false;
          this.completedSections.riskManagement = true;
          this.visibleSections.riskManagement = true;
          this.showToastMessage(this.describeRiskResolution(response?.data));
        },
        error: (error) => {
          this.addingRiskItem = false;
          console.error('Error saving risk item: ', error);
          this.showToastMessage('There was an error adding the issue to the risk register. Please try again.');
        }
      });
  }

  /** The suggestion list's `(addSuggestion)` output, unpacked. */
  /**
   * Manual add from the RISK MANAGEMENT header, mirroring TASK LIST's "Add New Task".
   *
   * Goes through the SAME dialog as accepting a suggestion, with no suggestion attached (D13) —
   * so the taxonomy, the facility selector and the missing-quote wording exist in one place and
   * cannot be relaxed in a second copy. This surface keeps the dialog rather than routing to the
   * detail page, deliberately: a doctor working through several suggestions on one request must
   * not be navigated away from it.
   */
  onAddRiskItemManually(): void {
    this.addRiskItem(new MouseEvent('click'), null);
  }

  onAddRiskSuggestion(payload: { event: MouseEvent; suggestion: RiskSuggestion }) {
    this.addRiskItem(payload.event, payload.suggestion);
  }

  /**
   * Confirms what was stored, naming the facility on the row that came back.
   *
   * THE RECONCILIATION HALF OF THIS IS GONE. It used to compare the dialog's §4 preview against
   * the server's `resolvedBy` and warn when they differed, which mattered while the server
   * resolved the facility itself and could overrule the screen. The client now decides and the
   * server stores it, so the two can no longer disagree and the warning could only ever have
   * contradicted a choice the doctor made on purpose.
   */
  private describeRiskResolution(item: any): string {
    if (!item) return 'Issue added to the risk register.';
    const facility = [item.hospitalName, item.wardName].filter((part: any) => !!part).join(' — ');
    // 'Chosen by you on the add form' when the doctor picked it, or the §4 branch that supplied
    // the default when they left it alone.
    const branch = riskResolutionLabel(item.resolvedBy, item.location);
    return facility
      ? `Issue added to the risk register — ${facility} (${branch}).`
      : `Issue added to the risk register — ${branch}.`;
  }

  canAddAllergy(allergy: string) {
    return this.patient.allergies.filter((a: any) => a.allergy === allergy).length === 0;
  }

  canAddMedication(medication: string) {
    return this.medicationAutocomplete?.medicationList.filter((item: any) => item.name.toLowerCase() === medication.toLowerCase()).length === 0;
  }

  addMedicationToPrescription(event: MouseEvent, medication: any) {
    event.preventDefault();
    event.stopPropagation();
    this.medicationAutocomplete?.addMedicationItem(medication);
  }

  toggleLabTestSelection(event: any, labTest: any) {
    // This method can be extended to handle lab test selection logic
    // For now, it's a placeholder for future functionality
    console.log('Lab test selection toggled:', labTest, event.checked);
  }

  onSuggestedLabTestsChanged(checkedItems: LabTestChecklistItem[]) {
    // Update selectedLabTests with search results (unified list)
    this.selectedLabTests = checkedItems;
    // Keep suggestedLabTestChecklistItems for backward compatibility if needed
    this.suggestedLabTestChecklistItems = checkedItems;
    console.log('Lab tests changed:', checkedItems);
  }

  onSuggestedLabTestsSelectionChanged(checkedTests: any[]) {
    // Handle when selected lab tests change
    console.log('Lab tests selection changed:', checkedTests);
  }

  hasSelectedLabTests(): boolean {
    return this.selectedLabTests.some(item => item.checked);
  }

  getSelectedLabTestsCount(): number {
    return this.selectedLabTests.filter(item => item.checked).length;
  }

  private loadLabOrderGroupUsers(): void {
    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        if (users?.length) {
          // Filter to only doctors
          this.labOrderGroupUsers = users.filter(u => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50');
          // Log to verify preferredLabTests are included
          console.log('[AIRequestDetail] Loaded lab order group users:', this.labOrderGroupUsers.map(u => ({
            userId: u.userId,
            name: `${u.title} ${u.name} ${u.surname}`,
            hasPreferredLabTests: !!u.preferredLabTests,
            preferredLabTestsCount: Array.isArray(u.preferredLabTests) ? u.preferredLabTests.length : 0
          })));
          this.determineLabOrderPhysicianSelection();
        }
      });
  }

  private determineLabOrderPhysicianSelection(): void {
    if (this.labOrderGroupUsers.length === 0) {
      this.selectedLabOrderPhysicianId = null;
      this.showLabOrderPhysicianSelection = false;
      return;
    }

    if (this.labOrderGroupUsers.length === 1) {
      // Only one physician - use as default and don't show selection
      this.selectedLabOrderPhysicianId = this.labOrderGroupUsers[0].userId;
      this.showLabOrderPhysicianSelection = false;
      if (this.selectedLabOrderPhysicianId) {
        this.loadLabTestBundlesForPhysician(this.selectedLabOrderPhysicianId);
      }
      return;
    }

    // Multiple physicians - check if current user is a doctor
    const currentUser = this.userService.user;
    const isCurrentUserPhysician = this.labOrderGroupUsers.findIndex(gu => gu.userId === currentUser.id) > -1;

    if (isCurrentUserPhysician) {
      // Current user is a physician - default to them
      this.selectedLabOrderPhysicianId = currentUser.id;
      this.showLabOrderPhysicianSelection = false;
      if (this.selectedLabOrderPhysicianId) {
        this.loadLabTestBundlesForPhysician(this.selectedLabOrderPhysicianId);
      }
    } else {
      // Current user is not a physician - show dropdown, default to group owner
      const groupOwner = this.labOrderGroupUsers.find(user => user.userId === this.groupService.group?.userId) || this.labOrderGroupUsers[0];
      this.selectedLabOrderPhysicianId = groupOwner?.userId || null;
      this.showLabOrderPhysicianSelection = true;
      if (this.selectedLabOrderPhysicianId) {
        this.loadLabTestBundlesForPhysician(this.selectedLabOrderPhysicianId);
      }
    }
  }

  onLabOrderPhysicianSelectionChanged(physicianId: string): void {
    this.selectedLabOrderPhysicianId = physicianId;
    this.loadLabTestBundlesForPhysician(physicianId);
  }

  private loadExistingLabOrderForAIRequest(): void {
    // Check if there's an existing lab order for this AI request
    if (!this.editingRequest?.id) return;

    this.patientLabService.loadLabOrdersByPatient(this.patient.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const labOrders = response?.data || [];
          // Find lab order associated with this AI request
          const existingLabOrder = labOrders.find((order: any) => order.aiRequestId === this.editingRequest.id);
          this.labOrderForThisAIRequest = existingLabOrder || null;

          if (existingLabOrder && existingLabOrder.userId) {
            // Set the physician from the existing lab order
            this.selectedLabOrderPhysicianId = existingLabOrder.userId;
            // Load bundles for that physician, then patch the form
            this.loadLabTestBundlesForPhysician(existingLabOrder.userId, () => {
              // Now patch the lab tests form with the existing order's tests
              if (existingLabOrder.labTests && Array.isArray(existingLabOrder.labTests)) {
                this.selectedLabTests = existingLabOrder.labTests.map((test: any) => ({
                  test: test,
                  checked: true,
                  category: test.category || 'General',
                  specimenSource: test.meta?.specimenSource || null
                }));
                this.labTestsForm.patchValue({
                  orderDate: existingLabOrder.orderDate ? new Date(existingLabOrder.orderDate) : new Date(),
                  priority: existingLabOrder.priority || 'routine',
                  labOrderNotes: existingLabOrder.notes || ''
                });
                this.updateAllOtherTests();
              } else {
                // No existing lab tests, just patch the form normally
                this.labTestsForm.patchValue(this.editingRequest);
              }
            });
          } else {
            // No existing lab order, patch normally
            this.labTestsForm.patchValue(this.editingRequest);
          }
        },
        error: (error) => {
          console.error('[AIRequestDetail] Error loading lab orders:', error);
          // On error, just patch normally
          this.labTestsForm.patchValue(this.editingRequest);
        }
      });
  }

  private loadLabTestBundles(profile: any): void {
    if (profile?.preferredLabTests) {
      this.labTestBundles = profile.preferredLabTests;
      // Extract all lab tests from bundles for display
      this.userPreferredLabTests = profile.preferredLabTests.flatMap((bundle: any) => bundle.labTests || []);
      this.updateLabTestChecklistItems();
      this.updateAllOtherTests();
    }
  }

  private loadLabTestBundlesForPhysician(physicianId: string | null, callback?: () => void): void {
    if (!physicianId) return;

    // If loading for current user, use the observable
    if (physicianId === this.userProfile?.id) {
      this.userService.userProfile$
        .pipe(takeUntil(this.destroy$))
        .subscribe((profile) => {
          this.loadLabTestBundles(profile);
          if (callback) callback();
        });
    } else {
      // Find physician from group users - they should have preferredLabTests if available
      const physician = this.labOrderGroupUsers.find(gu => gu.userId === physicianId);
      if (physician?.preferredLabTests && Array.isArray(physician.preferredLabTests) && physician.preferredLabTests.length > 0) {
        this.labTestBundles = physician.preferredLabTests;
        this.userPreferredLabTests = physician.preferredLabTests.flatMap((bundle: any) => bundle.labTests || []);
        this.updateLabTestChecklistItems();
        this.updateAllOtherTests();
        console.log('[AIRequestDetail] Loaded preferred lab tests from group users for physician:', physicianId, 'Bundles:', this.labTestBundles.length);
        if (callback) callback();
      } else {
        console.warn('[AIRequestDetail] Physician preferred lab tests not available in group users for userId:', physicianId, 'Physician:', physician);
        if (callback) callback(); // Still call callback even if bundles not found
      }
    }
  }

  openLabOrderConfigDialog(event: Event): void {
    event.preventDefault();
    const physician = this.selectedLabOrderPhysician;
    const dialogRef = this.dialog.open(LabOrderConfigDialogComponent, {
      width: '500px',
      data: {
        currentUser: this.userProfile,
        targetUser: physician, // The physician whose lancetCode needs to be set
        currentGroup: this.selectedGroup,
        needsLancetCode: !physician?.lancetCode,
        isUpdatingOtherUser: this.isNonDoctorUser && physician?.userId !== this.userProfile?.id
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Reload user profile if updating current user
        if (result.lancetCode !== undefined && !result.targetUserId) {
          this.userService.getUserProfile().subscribe(profile => {
            this.userProfile = profile;
            this.cd.detectChanges();
          });
        }
        // Reload lab order group users to get updated physician lancetCode
        if (result.targetUserId) {
          // Trigger a fresh fetch from the server
          this.groupService.getGroupUsers().subscribe(() => {
            // The observable will automatically update via groupUsers$ subscription
            // Reload preferred lab tests for the currently selected physician
            if (this.selectedLabOrderPhysicianId) {
              this.loadLabTestBundlesForPhysician(this.selectedLabOrderPhysicianId);
            }
            this.cd.detectChanges();
          });
        }
      }
    });
  }

  async submitLabOrder() {
    // For non-doctor users, require physician selection
    if (this.isNonDoctorUser && !this.selectedLabOrderPhysicianId) {
      this.showToastMessage('Please select a requesting physician');
      return;
    }

    const hasIdOrPassportNumber = this.patient.idNumber && this.patient.idNumber.trim() !== '' || this.patient.passportNumber && this.patient.passportNumber.trim() !== '';
    if (!hasIdOrPassportNumber) {
      this.showToastMessage('This patient does not have an ID or passport number which is required to submit a lab order. Please enter an ID or passport number to submit a lab order.');
      return;
    }

    this.analyticsService.trackButtonClick('AI Request Submit Lab Order');

    // Get selected tests from unified selectedLabTests array
    let selectedTests = this.selectedLabTests
      .filter(item => item.checked)
      .map(item => item.test);

    if (selectedTests.length === 0) {
      this.showToastMessage('Please select at least one lab test to submit.');
      return;
    }

    // Ensure meta information (including specimenSource) is preserved on test objects
    selectedTests = selectedTests.map((test: any) => {
      // Find the corresponding checklist item to get specimenSource
      const checklistItem = this.selectedLabTests.find(item => {
        const normalizeTest = (t: any) => t?.orderCode || t?.code || t?.id;
        return normalizeTest(item.test) === normalizeTest(test);
      });

      // Ensure meta object exists and includes specimenSource if available
      if (!test.meta) {
        test.meta = {};
      }
      if (checklistItem?.specimenSource !== undefined) {
        test.meta.specimenSource = checklistItem.specimenSource;
      } else if ((test as any).meta?.specimenSource) {
        // Preserve existing meta.specimenSource if already present
        test.meta.specimenSource = (test as any).meta.specimenSource;
      }

      return test;
    });

    try {
      const formData = this.labTestsForm.value;
      const labOrderData = {
        patientId: this.patient.id,
        groupId: patientGroupId(this.patient, this.selectedGroup.id),
        aiRequestId: this.editingRequest.id,
        labTests: selectedTests,
        orderDate: formData.orderDate.toISOString(),
        priority: formData.priority,
        notes: formData.labOrderNotes,
        labProvider: 'lancet',
        // Pass physicianId if selected (for non-doctor users ordering on behalf of a physician)
        physicianId: this.selectedLabOrderPhysicianId || this.userProfile?.id
      };

      const response: any = await this.patientLabService.createLabOrder(labOrderData).toPromise();

      if (response?.success) {
        this.showToastMessage(`Lab order submitted successfully with ${selectedTests.length} test(s).`);
        console.log('Lab order created:', response.data);
        this.labOrderForThisAIRequest = response.data || labOrderData;
        this.completedSections.labTests = true;
        this.visibleSections.labTests = true;
      } else {
        this.showToastMessage('Failed to submit lab order. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting lab order:', error);
      this.showToastMessage('Failed to submit lab order. Please try again.');
    }
  }

  convertToChecklistItems(suggestedLabTests: any): LabTestChecklistItem[] {
    if (!suggestedLabTests?.labTests || !Array.isArray(suggestedLabTests.labTests)) {
      return [];
    }

    return suggestedLabTests.labTests.map((suggestion: any) => ({
      test: suggestion.item,
      checked: true, // Pre-tick suggested lab tests
      category: suggestion.item.category || 'General'
    }));
  }

  updateLabTestChecklistItems() {
    // Always show search - initialize with empty selection
    this.suggestedLabTestChecklistItems = [];
    this.selectedLabTests = [];
    this.showIndividualTests = true;

    // Initialize with empty selection
    this.selectedBundles = [];
    this.previousSelectedBundles = []; // Initialize previous selection
    this.labTestsForm.patchValue({ selectedBundles: [] });

    // Check if AI has suggested bundles
    if (this.editingRequest?.suggestedLabTests?.labTestBundles) {
      const aiSuggestedBundleIds = new Set();
      this.editingRequest.suggestedLabTests.labTestBundles.forEach((suggestion: any) => {
        if (suggestion.item?.id) {
          aiSuggestedBundleIds.add(suggestion.item.id);
        }
      });

      // Pre-select AI-suggested bundles
      this.selectedBundles = this.labTestBundles.filter((bundle: any) =>
        aiSuggestedBundleIds.has(bundle.id)
      );
      this.previousSelectedBundles = [...this.selectedBundles]; // Initialize previous selection

      // Update the form control
      this.labTestsForm.patchValue({ selectedBundles: this.selectedBundles });

      if (this.selectedBundles.length > 0) {
        this.onBundleSelectionChanged(this.selectedBundles);
      }
    }

    // Also check if AI has suggested individual lab tests (not bundles)
    if (this.editingRequest?.suggestedLabTests?.labTests) {
      const aiSuggestedTests = this.convertToChecklistItems(this.editingRequest.suggestedLabTests);
      // Merge AI-suggested tests with existing selectedLabTests
      const normalizeTest = (test: any) => test?.orderCode || test?.code || test?.id;
      const existingTestCodes = new Set(
        this.selectedLabTests.map(item => normalizeTest(item.test))
      );

      const newTests = aiSuggestedTests.filter(item => {
        const testCode = normalizeTest(item.test);
        return testCode && !existingTestCodes.has(testCode);
      });

      this.selectedLabTests = [...this.selectedLabTests, ...newTests];
    }
  }

  private updateAllOtherTests(): void {
    // Helper to normalize test for comparison (same as backend)
    const normalizeTest = (test: any) => test?.orderCode || test?.code || test?.id;

    // Get all tests from selected bundles (deduplicated)
    const selectedBundleTests = this.selectedBundles.flatMap((bundle: any) => bundle.labTests || []);
    const selectedTestCodes = new Set(selectedBundleTests.map((t: any) => normalizeTest(t)));

    // Get all tests from all bundles (deduplicated)
    const allTests = this.labTestBundles.flatMap((bundle: any) => bundle.labTests || []);
    const uniqueAllTestsMap = new Map<string, any>();
    allTests.forEach((test: any) => {
      const testKey = normalizeTest(test);
      if (testKey && !uniqueAllTestsMap.has(testKey)) {
        uniqueAllTestsMap.set(testKey, test);
      }
    });
    const uniqueAllTests = Array.from(uniqueAllTestsMap.values());

    // Filter out tests that are already in selected bundles (using orderCode for comparison)
    this.allOtherTests = uniqueAllTests
      .filter(test => {
        const testCode = normalizeTest(test);
        return testCode && !selectedTestCodes.has(testCode);
      })
      .map(test => ({
        test: test,
        checked: false,
        category: (test as any).category || 'General'
      }));
  }

  onBundleSelectionChanged(selectedBundles: any[]): void {
    // Helper to normalize test for comparison (same as backend)
    const normalizeTest = (test: any) => test?.orderCode || test?.code || test?.id;

    // Find bundles that were deselected (in previous but not in current)
    const deselectedBundles = this.previousSelectedBundles.filter(
      prevBundle => !selectedBundles.some(currBundle => currBundle.id === prevBundle.id)
    );

    // If bundles were deselected, remove their tests from selectedLabTests
    if (deselectedBundles.length > 0) {
      // Get all test codes from deselected bundles
      const deselectedBundleTests = deselectedBundles.flatMap((bundle: any) => bundle.labTests || []);
      const deselectedTestCodes = new Set(
        deselectedBundleTests.map((test: any) => normalizeTest(test))
      );

      // Remove tests that came from deselected bundles
      // Keep tests that were added via search (not from any bundle)
      this.selectedLabTests = this.selectedLabTests.filter(item => {
        const testCode = normalizeTest(item.test);
        // Keep the test if it's not from a deselected bundle
        return !deselectedTestCodes.has(testCode);
      });
    }

    // Update selected bundles
    this.selectedBundles = selectedBundles;
    this.previousSelectedBundles = [...selectedBundles]; // Store for next comparison

    // Update the form control
    this.labTestsForm.patchValue({ selectedBundles: selectedBundles });

    if (selectedBundles.length > 0) {
      // Get all tests from selected bundles
      const allBundleTests = selectedBundles.flatMap((bundle: any) => bundle.labTests || []);

      // Deduplicate tests using orderCode (same normalization as backend)
      const uniqueTestsMap = new Map<string, any>();
      allBundleTests.forEach((test: any) => {
        const testKey = normalizeTest(test);
        if (testKey && !uniqueTestsMap.has(testKey)) {
          uniqueTestsMap.set(testKey, test);
        }
      });

      // Convert map back to array of unique tests
      const bundleTests = Array.from(uniqueTestsMap.values());

      // Get existing test codes from currently selected tests
      const existingTestCodes = new Set(
        this.selectedLabTests.map(item => normalizeTest(item.test))
      );

      // Add bundle tests that aren't already in selectedLabTests
      const newBundleTests = bundleTests
        .filter(test => !existingTestCodes.has(normalizeTest(test)))
        .map(test => ({
          test: test,
          checked: true,
          category: test.category || 'General',
          specimenSource: (test as any).meta?.specimenSource || null
        }));

      // Merge with existing selected tests
      this.selectedLabTests = [...this.selectedLabTests, ...newBundleTests];
    }

    this.updateAllOtherTests();
  }

  onLabTestsChanged(checkedItems: LabTestChecklistItem[]): void {
    // The lab-test-selector emits the full current state of the checklist
    // This includes both bundle tests and searched tests
    // So we can just replace the entire list
    this.selectedLabTests = checkedItems;
  }

  onLabTestsSelectionChanged(checkedTests: any[]): void {
    // Handle individual test selection changes if needed
    console.log('Lab tests selection changed:', checkedTests);
  }

  onOtherTestsChanged(checkedItems: LabTestChecklistItem[]): void {
    this.allOtherTests = checkedItems;

    // Helper to normalize test for comparison (same as backend)
    const normalizeTest = (test: any) => test?.orderCode || test?.code || test?.id;

    const selectedTests = checkedItems
      .filter(item => item.checked)
      .map(item => item.test);

    // Get current test codes from selectedLabTests
    const currentTestCodes = new Set(
      this.selectedLabTests.map(item => normalizeTest(item.test))
    );

    // Only add tests that aren't already in selectedLabTests
    const newTests = selectedTests
      .filter(test => {
        const testCode = normalizeTest(test);
        return testCode && !currentTestCodes.has(testCode);
      })
      .map(test => ({
        test: test,
        checked: true,
        category: (test as any).category || 'General',
        specimenSource: (test as any).meta?.specimenSource || null
      }));

    // Merge with existing selected tests
    this.selectedLabTests = [...this.selectedLabTests, ...newTests];
  }

  onOtherTestsSelectionChanged(checkedTests: any[]): void {
    console.log('Other tests selection changed:', checkedTests);
  }


  async aiRegenerateNotes(event: MatSelectChange) {
    this.analyticsService.trackButtonClick(`AI Request Regenerate Notes`);
    const confirmed = await this.showConfirmationDialog(undefined, 'Do you want to regenerate the notes using the new note type?');
    if (confirmed) {
      const noteTypeId = event.value;
      this.subscribeToOutputUpdates('notes');
      this.notesForm.patchValue({ notes: '' });
      this.aiService.generateNotes(this.editingRequest.id, noteTypeId)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
  }

  selectCodingBundle(event: MatSelectChange) {
    const codingBundleId = event.value;
    const selectedCodingBundle = this.codingBundles.filter(cb => cb.id === codingBundleId)[0]
    this.selectedProcedureCodes = selectedCodingBundle?.procedureCodes.map((c: any) => { return { ...c, paidCash: selectedCodingBundle.paidCash, noCharge: selectedCodingBundle.noCharge } }) || [];
    this.addingNote.procedureCodes = this.selectedProcedureCodes;
    this.notesForm.patchValue({ paidCash: selectedCodingBundle.paidCash, noCharge: selectedCodingBundle.noCharge });

  }

  selectMotivationCodingBundle(event: MatSelectChange) {
    const codingBundleId = event.value;
    const selectedCodingBundle = this.codingBundles.filter(cb => cb.id === codingBundleId)[0]
    this.selectedMotivationProcedureCodes = selectedCodingBundle?.procedureCodes.map((c: any) => { return { ...c, paidCash: selectedCodingBundle.paidCash, noCharge: selectedCodingBundle.noCharge } }) || [];
    // Picking a different bundle is a deliberate user edit, so it REPLACES the selection —
    // unlike the load path, which restores what was on the letter (§1.1).
    this.syncMotivationProcedureCodesToForm();
  }

  addICD10Code(event: MouseEvent, code: any) {
    event.preventDefault();
    event.stopPropagation();

    const currentCodes = this.selectedPatientIcdCodes;
    const newCode = { code: code.code, description: code.description };
    currentCodes.push(newCode);

    const currentDiagnosis = this.diagnosisForm.get('diagnosis')?.value;
    if(!currentDiagnosis) {
      this.diagnosisForm.patchValue({ diagnosis: code.description });
    }
    // Use the reusable component's addChip method
    this.icdCodeSelector.addChip(code).then(() => {
      // After the chip is added, trigger change detection if needed
      this.cd.detectChanges();
    });
  }


  onPatientIcdCodesChanged(codes: IcdCode[]) {
    this.selectedPatientIcdCodes = codes;

    this.patientService.updatePatient(this.patient.id, { id: this.patient.id, icdCodes: this.selectedPatientIcdCodes, diagnosis: this.diagnosisForm.get('diagnosis')?.value })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient ICD-10 codes updated successfully');
          this.completedSections.fileUpdate = true;
          this.visibleSections.fileUpdate = true;
        },
        error: (error) => {
          console.error('Error updating patient ICD-10 codes: ', error);
          this.showToastMessage('There was an error updating the patient ICD-10 codes. Please try again.');
        }
      });
  }


  onPatientAllergiesChanged(allergies: any[]) {
    this.selectedAllergies = allergies;

    this.patientService.updatePatient(this.patient.id, { id: this.patient.id, allergies: this.selectedAllergies })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient allergies updated successfully');
        },
        error: (error) => {
          console.error('Error updating patient allergies: ', error);
          this.showToastMessage('There was an error updating the patient allergies. Please try again.');
        }
      });
  }

  onNoAllergiesChanged(noAllergies: boolean) {
    this.noAllergies = noAllergies;

    this.patientService.updatePatient(this.patient.id, { id: this.patient.id, noAllergies: this.noAllergies })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient allergy status updated successfully');
        },
        error: (error) => {
          console.error('Error updating patient allergy status: ', error);
          this.showToastMessage('There was an error updating the patient allergy status. Please try again.');
        }
      });
  }

  onPatientMedicalHistoryChanged(history: any[]) {
    this.selectedMedicalHistory = history;

    this.patientService.updatePatient(this.patient.id, { id: this.patient.id, medicalHistory: this.selectedMedicalHistory })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient medical history updated successfully');
        },
        error: (error) => {
          console.error('Error updating patient medical history: ', error);
          this.showToastMessage('There was an error updating the patient medical history. Please try again.');
        }
      });
  }

  onNoMedicalHistoryChanged(noMedicalHistory: boolean) {

    this.patientService.updatePatient(this.patient.id, { id: this.patient.id, noMedicalHistory })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient medical history status updated successfully');
        },
        error: (error) => {
          console.error('Error updating patient medical history status: ', error);
          this.showToastMessage('There was an error updating the patient medical history status. Please try again.');
        }
      });
  }

  onPatientMedicationHistoryChanged(history: any[]) {
    this.selectedMedicationHistory = history;

    this.patientService.updatePatient(this.patient.id, { id: this.patient.id, medicationHistory: this.selectedMedicationHistory })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient medication list updated successfully');
        },
        error: (error) => {
          console.error('Error updating patient medication list: ', error);
          this.showToastMessage('There was an error updating the patient medication list. Please try again.');
        }
      });
  }

  onNoMedicationHistoryChanged(noMedicationHistory: boolean) {

    this.patientService.updatePatient(this.patient.id, { id: this.patient.id, noMedicationHistory })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient medication status updated successfully');
        },
        error: (error) => {
          console.error('Error updating patient medication status: ', error);
          this.showToastMessage('There was an error updating the patient medication status. Please try again.');
        }
      });
  }

  onIcdCodesChanged(codes: IcdCode[]) {
    this.selectedMotivationIcdCodes = codes;
  }

  onAllergiesChanged(allergies: any[]) {
    this.selectedAllergies = allergies;
  }

  onMedicalHistoryChanged(history: any[]) {
    this.selectedMedicalHistory = history;
  }

  onMedicationHistoryChanged(history: any[]) {
    this.selectedMedicationHistory = history;
  }

  togglePaidCash(event: MatSlideToggleChange) {
    this.selectedProcedureCodes = this.selectedProcedureCodes.map((c: any) => { return { ...c, paidCash: event.checked } });
    if (event.checked) {
      this.selectedProcedureCodes = this.selectedProcedureCodes.map((c: any) => { return { ...c, noCharge: false } });
      this.notesForm.patchValue({ noCharge: false });
    }
  }

  toggleNoCharge(event: MatSlideToggleChange) {
    this.selectedProcedureCodes = this.selectedProcedureCodes.map((c: any) => { return { ...c, noCharge: event.checked } });
    if (event.checked) {
      this.selectedProcedureCodes = this.selectedProcedureCodes.map((c: any) => { return { ...c, paidCash: false } });
      this.notesForm.patchValue({ paidCash: false });
    }
  }

  toggleCodeStatus(note: any, type: string) {
    switch (type) {
      case 'up-front':
        if (note.paidCash) {
          note.noCharge = false;
          this.notesForm.patchValue({ noCharge: false });
          note.procedureCodes = note.procedureCodes.map((c: any) => { return { ...c, noCharge: false, paidCash: true } });
        }
        break;
      case 'no-charge':
        if (note.noCharge) {
          note.paidCash = false;
          this.notesForm.patchValue({ paidCash: false });
          note.procedureCodes = note.procedureCodes.map((c: any) => { return { ...c, noCharge: true, paidCash: false } });
        }
        break;
    }
  }

  /* loadTimelineData(): void {
    combineLatest({
      notes: this.patientNoteService.patientNotes$,
      ah: this.patientAdmissionHistoryService.patientAdmissionRecords$
    }).pipe(
      map(({ notes, ah }) => {
        return this.combineAndSortTimelineData(notes || [], ah || []);
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (combinedData) => {
        this.timelineData = combinedData;
        this.filterTimeline();
      },
      error: (err) => {
        console.error('Error loading notes or forms:', err);
        this.timelineData = [];  // Optionally reset or handle errors as needed
        this.filterTimeline();
      }
    });
  }

  combineAndSortTimelineData(notes: any, admissionHistory: any): any[] {
    const combined = [
      ...notes.map((note: any) => ({ ...note, type: 'note', date: note.date })),
      ...admissionHistory.map((ah: any) => ({ ...ah, date: ah.dischargeDate, type: 'admission-record' }))
    ];

    // Sort the combined array by date in descending order
    return _.orderBy(combined, ['date'], ['desc']);
  }

  filterTimeline() {
    const { startDate, endDate } = this.reportForm?.value;
    this.filteredTimelineData = this.timelineData?.filter(item => moment(item.date).isBetween(moment(startDate), moment(endDate))) || [];
  } */

  aiGeneratePatientReport() {
    this.analyticsService.trackButtonClick(`AI Request Generate Patient Report`);
    const selectedOptions = this.selectedTimelineItems?.selectedOptions.selected.map(item => item.value);
    const { noteTypeId } = this.reportForm.value;
    this.subscribeToOutputUpdates('patientReport')
    this.aiService.generatePatientReport(this.editingRequest.id, selectedOptions!, noteTypeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  async aiGenerateLetterOfMotivation() {
    // Check if patient needs diagnosis/ICD-10 codes
    const canProceed = await this.checkDiagnosisAndIcdCodes();
    if (!canProceed) {
      return; // User cancelled or skipped
    } else {
      this.selectedMotivationIcdCodes = this.selectedPatientIcdCodesDialog;
    }
    this.analyticsService.trackButtonClick(`AI Request Generate Letter Of Motivation`);
    // The server persists these codes in the same save() that writes the letter, but mirror them
    // onto the form here too so a Save Request that never regenerates still carries them (§1.1).
    this.syncMotivationProcedureCodesToForm();
    const { medicalAid, medicalAidNumber, medicalAidPlan, motivationReason, surgicalMotivationLetter } = this.lomForm.value;
    this.subscribeToOutputUpdates('letterOfMotivation')
    this.aiService.generateLetterOfMotivation(this.editingRequest.id, medicalAid, medicalAidNumber, medicalAidPlan, motivationReason, surgicalMotivationLetter, this.selectedMotivationProcedureCodes, this.selectedMotivationIcdCodes)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  async aiGenerateRadiologyRequest() {
    // Only open dialog when patient lacks diagnosis/ICD; otherwise keep user's selection from the panel
    const needsDialog = this.needsDiagnosisOrIcdCodes();
    if (needsDialog) {
      const canProceed = await this.checkDiagnosisAndIcdCodes();
      if (!canProceed) {
        return;
      }
      this.selectedMotivationIcdCodes = this.selectedPatientIcdCodesDialog;
    }
    this.analyticsService.trackButtonClick(`AI Request Generate Radiology Request`);
    this.subscribeToOutputUpdates('radiologyRequest');
    this.aiService.generateRadiologyRequest(this.editingRequest.id, this.selectedMotivationIcdCodes)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  private async addNewColleague(name: string, email: string, speciality: string): Promise<void> {
    return new Promise((resolve) => {
      this.addingColleague = { title: '', name, surname: '', speciality, email, contactNumber: '' };

      const dialogRef = this.dialog.open(this.addColleagueDialog!, { width: '600px' }); // Open the add colleague dialog

      dialogRef.afterClosed().subscribe(colleagueDetails => {
        if (colleagueDetails) {
          this.colleagueService.addColleague(this.addingColleague)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              (response: any) => {
                this.showToastMessage('Colleague added successfully');
                resolve();
              },
              (error) => {
                console.error('Error adding colleague:', error);
                this.showToastMessage('There was an error adding the colleague. Please try again.');
                resolve();
              }
            );
        }
      })
    });
  }

  async aiGenerateLetterToColleague() {
    const { colleagueName, colleagueEmail, colleagueSpeciality } = this.colleagueForm.value;
    const existingColleague = this.colleagues.find(c => `${c.title} ${c.name} ${c.surname}` === colleagueName && c.email === colleagueEmail);

    if (!existingColleague && (colleagueName || colleagueEmail)) {
      const addColleague = await this.showConfirmationDialog('Add New Colleague?', 'This colleague does not exist in your colleague list. Would you like to add them now?', [], 'No', 'Yes');

      if (addColleague) {
        await this.addNewColleague(colleagueName, colleagueEmail, colleagueSpeciality);
      }

      this.generateLetterToColleague();

    } else {
      this.generateLetterToColleague();
    }
  }

  private generateLetterToColleague() {
    this.subscribeToOutputUpdates('letterToColleague');
    const { colleagueName, colleagueEmail, colleagueSpeciality, letterReason } = this.colleagueForm.value;
    this.aiService.generateLetterToColleague(this.editingRequest.id, colleagueName, colleagueEmail, colleagueSpeciality, letterReason)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.analyticsService.trackButtonClick(`AI Request Generate Letter To Colleague`);
  }

  aiGenerateLetterToGP() {
    this.subscribeToOutputUpdates('letterToGP');
    const { generalPractitioner, generalPractitionerEmail, generalPractitionerPhone } = this.gpForm.value
    const type = this.selectedGroup?.groupSpeciality === 'General Practice' ? 'Specialist' : 'GP'
    this.aiService.generateLetterToGP(this.editingRequest.id, generalPractitioner, generalPractitionerEmail, generalPractitionerPhone, type)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.analyticsService.trackButtonClick(`AI Request Generate Letter To ${type}`);

  }

  aiGenerateLetterToPatient() {
    this.subscribeToOutputUpdates('letterToPatient')
    this.aiService.generateLetterToPatient(this.editingRequest.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.analyticsService.trackButtonClick(`AI Request Generate Letter To Patient`);
  }

  isGeneratingSickLetter: boolean = false;
  aiGenerateLetterToPatientWork() {
    this.isGeneratingSickLetter = true;
    this.subscribeToOutputUpdates('letterToPatientWork')
    const { patientEmployer } = this.patientWorkForm.value;
    this.aiService.generateLetterToPatientWork(this.editingRequest.id, patientEmployer)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.analyticsService.trackButtonClick(`AI Request Generate Letter To Patient Work`);
  }

  /*  aiGenerateDischargeSummary() {
     this.subscribeToOutputUpdates('dischargeSummary')
     const { admissionDate, dischargeDate } = this.dischargeForm.value;
     this.aiService.generateDischargeSummary(this.editingRequest.id, admissionDate, dischargeDate)
       .pipe(takeUntil(this.destroy$))
       .subscribe();
   } */

  aiGenerateTaskList() {
    this.subscribeToOutputUpdates('suggestedAdminTasks')
    this.aiService.generateTaskList(this.editingRequest.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.analyticsService.trackButtonClick(`AI Request Generate Task List`);
  }


  aiGenerateProcedureCodeSuggestions() {
    this.subscribeToOutputUpdates('suggestedProcedureCodes')
    this.aiService.generateProcedureCodeSuggestions(this.editingRequest.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.analyticsService.trackButtonClick(`AI Request Generate PC Suggestions`);
  }

  /* aiGenerateProcedureCodeSuggestionsForDialog() {
    this.analyticsService.trackButtonClick(`AI Request Generate PC Suggestions For Dialog`);
    
    // Generate procedure code suggestions for the dialog
    this.aiService.generateProcedureCodeSuggestions(this.editingRequest.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: any) => {
        if (response && response.codes) {
          this.addingNote.suggestedProcedureCodes = response;
        }
      });
  } */


  aiPerformUserPrompt(type: 'notes' | 'patientReport' | 'letterOfMotivation' | 'radiologyRequest' | 'letterToColleague' | 'letterToGP' | 'letterToPatient' | 'letterToPatientWork' | 'suggestedLabTests', event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.analyticsService.trackButtonClick(`AI Request User Prompt For ${type}`);
    if ((event as KeyboardEvent)?.shiftKey) return;
    let form;
    switch (type) {
      case 'notes':
        form = this.notesForm;
        break;
      case 'patientReport':
        form = this.reportForm;
        break;
      case 'letterOfMotivation':
        form = this.lomForm;
        break;
      case 'radiologyRequest':
        form = this.radiologyForm;
        break;
      case 'letterToColleague':
        form = this.colleagueForm;
        break;
      case 'letterToGP':
        form = this.gpForm;
        break;
      case 'letterToPatient':
        form = this.patientForm;
        break;
      case 'letterToPatientWork':
        form = this.patientWorkForm;
        break;
      case 'suggestedLabTests':
        form = this.labTestsForm;
        break;
      /*  case 'dischargeSummary':
         form = this.dischargeForm;
         break; */

    }
    const prompt = form?.get(`${type}Prompt`)?.value
    if (!prompt) return;

    form?.get(`${type}Prompt`)?.patchValue('');
    form?.get(`${type}Prompt`)?.markAsPristine();

    this.subscribeToOutputUpdates(type as any);
    form?.get(type)?.patchValue('');
    this.aiService.performUserPrompt(this.editingRequest.id, prompt, type)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  private subscribeToOutputUpdates(type: 'notes' | 'suggestedAdminTasks' | 'patientReport' | 'letterOfMotivation' | 'radiologyRequest' | 'letterToColleague' | 'letterToGP' | 'letterToPatient' | 'letterToPatientWork' | 'dischargeSummary' | 'suggestedProcedureCodes' | 'suggestedLabTests') {
    let outputStream = '';

    if (this.socketSubs) {
      for (const sub of this.socketSubs)
        sub.unsubscribe();
      outputStream = '';
    }


    if (type === 'notes') {
      const notesSub = this.socketService.listen('aiOutputUpdate-notes', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          outputStream += output.results || '';
          this.notesForm.patchValue({ notes: outputStream });
          setTimeout(() => {
            this.notesTextarea!.nativeElement.scrollTop = this.notesTextarea!.nativeElement.scrollHeight;
          }, 0);
          if (output.completed) {
            notesSub.unsubscribe();
            this.showAutoSaveConfirmation();
            this.completedSections.notes = true;
            this.visibleSections.notes = true;
          }
        });
    }

    if (type === 'patientReport') {
      const reportSub = this.socketService.listen('aiOutputUpdate-patientReport', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          outputStream += output.results || '';
          this.reportForm.patchValue({ patientReport: outputStream });
          setTimeout(() => {
            this.patientReportTextarea!.nativeElement.scrollTop = this.patientReportTextarea!.nativeElement.scrollHeight;
          }, 0);
          if (output.completed) {
            reportSub.unsubscribe();
            this.showAutoSaveConfirmation();
            this.completedSections.letterToPatient = true;
            this.visibleSections.letterToPatient = true;
          }
        });
    }


    if (type === 'letterOfMotivation') {
      const motivationSub = this.socketService.listen('aiOutputUpdate-letterOfMotivation', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          outputStream += output.results || '';
          this.lomForm.patchValue({ letterOfMotivation: outputStream });
          setTimeout(() => {
            this.letterOfMotivationTextarea!.nativeElement.scrollTop = this.letterOfMotivationTextarea!.nativeElement.scrollHeight;
          }, 0);
          if (output.completed) {
            motivationSub.unsubscribe();
            this.showAutoSaveConfirmation();
            this.completedSections.lom = true;
            this.visibleSections.lom = true;
          }
        });
    }

    if (type === 'radiologyRequest') {
      const radiologySub = this.socketService.listen('aiOutputUpdate-radiologyRequest', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          outputStream += output.results || '';
          this.radiologyForm.patchValue({ radiologyRequest: outputStream });
          setTimeout(() => {
            this.radiologyRequestTextarea!.nativeElement.scrollTop = this.radiologyRequestTextarea!.nativeElement.scrollHeight;
          }, 0);
          if (output.completed) {
            radiologySub.unsubscribe();
            this.showAutoSaveConfirmation();
            this.completedSections.radiologyRequest = true;
            this.visibleSections.radiologyRequest = true;
          }
        });
    }

    if (type === 'letterToColleague') {
      const colleagueSub = this.socketService.listen('aiOutputUpdate-letterToColleague', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          outputStream += output.results || '';
          this.colleagueForm.patchValue({ letterToColleague: outputStream });
          setTimeout(() => {
            this.letterToColleagueTextarea!.nativeElement.scrollTop = this.letterToColleagueTextarea!.nativeElement.scrollHeight;
          }, 0);
          if (output.completed) {
            colleagueSub.unsubscribe();
            this.showAutoSaveConfirmation();
            this.completedSections.letterToColleague = true;
            this.visibleSections.letterToColleague = true;
          }
        });
    }

    if (type === 'letterToGP') {
      const gpSub = this.socketService.listen('aiOutputUpdate-letterToGP', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          outputStream += output.results || '';
          this.gpForm.patchValue({ letterToGP: outputStream });
          setTimeout(() => {
            this.letterToGPTextarea!.nativeElement.scrollTop = this.letterToGPTextarea!.nativeElement.scrollHeight;
          }, 0);
          if (output.completed) {
            gpSub.unsubscribe();
            this.showAutoSaveConfirmation();
            this.completedSections.letterToGP = true;
            this.visibleSections.letterToGP = true;
          }
        });
    }

    if (type === 'letterToPatient') {
      const patientSub = this.socketService.listen('aiOutputUpdate-letterToPatient', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          outputStream += output.results || '';
          this.patientForm.patchValue({ letterToPatient: outputStream });
          setTimeout(() => {
            this.letterToPatientTextarea!.nativeElement.scrollTop = this.letterToPatientTextarea!.nativeElement.scrollHeight;
          }, 0);
          if (output.completed) {
            patientSub.unsubscribe();
            this.showAutoSaveConfirmation();
            this.completedSections.letterToPatient = true;
            this.visibleSections.letterToPatient = true;
          }
        });
    }


    if (type === 'letterToPatientWork') {
      const workSub = this.socketService.listen('aiOutputUpdate-letterToPatientWork', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          outputStream += output.results || '';
          this.patientWorkForm.patchValue({ letterToPatientWork: outputStream });
          setTimeout(() => {
            this.letterToPatientWorkTextarea!.nativeElement.scrollTop = this.letterToPatientWorkTextarea!.nativeElement.scrollHeight;
          }, 0);
          if (output.completed) {
            workSub.unsubscribe();
            this.showAutoSaveConfirmation();
            this.isGeneratingSickLetter = false;
            this.completedSections.letterToPatientWork = true;
            this.visibleSections.letterToPatientWork = true;
          }
        });
    }

    /*  if (type === 'dischargeSummary') {
       const dischargeSub = this.socketService.listen('aiOutputUpdate-dischargeSummary', 'patient-ai-request-detail-component')
         .pipe(takeUntil(this.destroy$))
         .subscribe(output => {
           outputStream += output.results || '';
           this.dischargeForm.patchValue({ dischargeSummary: outputStream });
           setTimeout(() => {
             this.dischargeSummaryTextarea!.nativeElement.scrollTop = this.dischargeSummaryTextarea!.nativeElement.scrollHeight;
           }, 0);
           if (output.completed) {
             dischargeSub.unsubscribe();
             this.showAutoSaveConfirmation();
           }
         });
     } */

    if (type === 'suggestedAdminTasks') {
      const taskSub = this.socketService.listen('aiOutputUpdate-suggestedAdminTasks', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          if (output.results) {
            this.editingRequest.suggestedAdminTasks = output.results;
            this.tasksForm.patchValue({ suggestedAdminTasks: output.results });
          }

          if (output.completed) {
            taskSub.unsubscribe();
            this.showAutoSaveConfirmation();
          }
        });
    }


    if (type === 'suggestedProcedureCodes') {
      const pcSub = this.socketService.listen('aiOutputUpdate-suggestedProcedureCodes', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          if (output.results) {
            this.editingRequest.suggestedProcedureCodes = output.results;
            this.notesForm.patchValue({ suggestedProcedureCodes: output.results });
            this.addingNote.suggestedProcedureCodes = output.results;
          }
          if (output.completed) {
            pcSub.unsubscribe();
            this.showAutoSaveConfirmation();
          }
        });
    }

    if (type === 'suggestedLabTests') {
      const labTestsSub = this.socketService.listen('aiOutputUpdate-suggestedLabTests', 'patient-ai-request-detail-component')
        .pipe(takeUntil(this.destroy$))
        .subscribe(output => {
          if (output.results) {
            this.editingRequest.suggestedLabTests = output.results;
            this.labTestsForm.patchValue({ suggestedLabTests: output.results });
            //this.completedSections.labTests = true;
            //this.visibleSections.labTests = true;

            // Update checklist items with user's preferred tests and AI suggestions
            this.updateLabTestChecklistItems();
          }
          if (output.completed) {
            labTestsSub.unsubscribe();
            this.showAutoSaveConfirmation();
          }
        });
    }

    this.socketSubs.push(
      this.socketService.listen('aiOutputError', 'patient-ai-request-detail-component')
        .subscribe(error => {
          console.error('AI output generation error:', error);
          // Notify user of error
          alert('Error generating AI output.');
        })
    );
  }

  showAutoSaveConfirmation() {
    this.showToastMessage('AI completed processing and the request was autosaved successfully.')
  }

  private getAllDetails() {

    return {
      ...this.notesForm.value,
      ...this.tasksForm.value,
      ...this.lomForm.value,
      ...this.radiologyForm.value,
      ...this.colleagueForm.value,
      ...this.gpForm.value,
      ...this.patientForm.value,
      ...this.patientWorkForm.value,
      //...this.dischargeForm.value,
      ...this.reportForm.value,
      ...this.medicationForm.value,
      completedSections: this.completedSections,
      visibleSections: this.visibleSections
    }
  }


  async saveRequest() {
    this.analyticsService.trackButtonClick(`AI Request Save Request`);
    // The patient this request is being saved for, taken before any dialog can wait on the user.
    const patientId = this.patient.id;

    // Check if patient needs diagnosis/ICD-10 codes
    const canProceed = await this.checkDiagnosisAndIcdCodes();
    if (!canProceed) {
      return; // User cancelled or skipped
    }

    const details = this.getAllDetails();
    const diagnosis = this.diagnosisForm.value;
    forkJoin([
      this.aiService.updateAIRequest(this.editingRequest.id, details),
      this.patientService.updatePatient(patientId, { id: patientId, ...diagnosis })
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient AI request updated successfully');
          this.returnToParent(false);
        },
        error: (error) => {
          console.error('Error updating patient AI request: ', error);
          this.showToastMessage('There was an error updating the patient AI request. Please try again.');
        }
      });
  }


  /** The saved letter to the patient, sent to (or withdrawn from) the patient's app (tasks/patient-app.md §6.1). */
  openPatientAppShare(): void {
    if (!this.editingRequest?.id || !this.patient?.id) return;
    this.dialog.open<PatientAppShareDialogComponent, PatientAppShareDialogData>(PatientAppShareDialogComponent, {
      width: '520px', maxHeight: '90vh', autoFocus: false,
      data: {
        patientId: this.patient.id,
        patientName: `${this.patient?.name || ''} ${this.patient?.surname || ''}`.trim(),
        entryTitle: 'Letter from your visit',
        sourceType: 'patient_letter',
        sourceId: this.editingRequest.id,
      },
    });
  }

  async deleteRequest() {
    this.analyticsService.trackButtonClick(`AI Request Delete Request`);
    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;

    this.aiService.deleteAIRequest(this.editingRequest.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient AI request deleted successfully');
          this.returnToParent(false)
        },
        error: (error) => {
          console.error('Error saving patient AI request: ', error);
          this.showToastMessage('There was an error deleting the patient AI request. Please try again.');
        }
      });
  }

  async returnToParent(showConfirmation: boolean = true) {
    if (showConfirmation) {
      const confirmed = await this.showConfirmationDialog('CONFIRM', 'Are you sure you want to leave this page? All unsaved changes will be lost.');
      if (!confirmed) return;
    }

    // Check if patient needs diagnosis/ICD-10 codes
    const canProceed = await this.checkDiagnosisAndIcdCodes();
    if (!canProceed) {
      return; // User cancelled or skipped
    }

    // Persist section state before navigating away
    this.persistSectionState();

    if (this.patient?.id) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patient.id}`] } }]);
    } else {
      let parentPath = 'list';
      const currentUrl = this.router.url;
      if (currentUrl.includes('schedule')) {
        parentPath = 'schedule';
      }
      if (currentUrl.includes('filters')) {
        parentPath = 'filters';
      }
      if (currentUrl.includes('coding')) {
        parentPath = 'coding';
      }
      this.router.navigate(['/patients', { outlets: { patient: null } }]).then(() => {
        this.router.navigate(['/patients', parentPath]);
      });
    }
  }

  handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }

  // Diagnosis/ICD-10 Dialog methods
  private needsDiagnosisOrIcdCodes(): boolean {
    if (!this.patient) return false;

    // Check if patient has no diagnosis or diagnosis is UNSPECIFIED
    const hasDiagnosis = this.patient.diagnosis &&
      this.patient.diagnosis.trim() !== '' &&
      this.patient.diagnosis.toLowerCase() !== 'unspecified';

    // Check if patient has no ICD-10 codes or only has Z00 codes
    const hasIcdCodes = this.patient.icdCodes && this.patient.icdCodes.length > 0;
    const hasOnlyZ00Codes = hasIcdCodes &&
      this.patient.icdCodes.every((code: any) => code.code.startsWith('Z00'));

    return !hasDiagnosis || !hasIcdCodes || hasOnlyZ00Codes;
  }

  private async checkDiagnosisAndIcdCodes(): Promise<boolean> {
    // If user has already chosen to skip during this action, allow it
    if (this.skipDiagnosisCheck) {
      this.skipDiagnosisCheck = false; // Reset for next time
      return true;
    }

    if (!this.needsDiagnosisOrIcdCodes()) {
      return true; // No popup needed
    }

    // Initialize dialog data - filter out Z00 codes
    this.selectedPatientIcdCodesDialog = (this.patient.icdCodes || []).filter((code: any) => !code.code.startsWith('Z00'));
    this.visibleDiagnosisFieldsDialog = [];
    this.diagnosisIcdForm.reset();

    // Set current diagnosis if it exists and is not UNSPECIFIED
    if (this.patient.diagnosis && this.patient.diagnosis.toLowerCase() !== 'unspecified') {
      this.diagnosisIcdForm.patchValue({ diagnosis: this.patient.diagnosis });
    }

    // Add additional diagnosis fields if they exist
    for (let i = 2; i <= 24; i++) {
      const fieldName = `diagnosis${i}`;
      if (this.patient[fieldName]) {
        this.addDiagnosisFieldToDialog(this.patient[fieldName]);
      }
    }

    // The patient stays put until the dialog is answered: what it saves belongs to this patient only.
    this.diagnosisDialogPatient = this.patient;
    const lockReason = `Save or skip the diagnosis and ICD-10 codes for ${this.patient.name} ${this.patient.surname} first.`;
    this.patientService.acquirePatientNavigationLock(lockReason);

    let result: any;
    try {
      this.diagnosisIcdDialogRef = this.dialog.open(this.diagnosisIcdDialog!, {
        width: '800px',
        disableClose: true
      });
      result = await this.diagnosisIcdDialogRef.afterClosed().toPromise();
    } finally {
      this.patientService.releasePatientNavigationLock(lockReason);
      this.diagnosisDialogPatient = null;
    }

    // Return true for both save and skip, false only if dialog was cancelled
    return result === 'save' || result === 'skip';
  }

  addDiagnosisFieldToDialog(value?: any): void {
    const nextIndex = this.visibleDiagnosisFieldsDialog.length + 2;
    if (nextIndex <= 24) {
      const fieldName = `diagnosis${nextIndex}`;
      this.diagnosisIcdForm.addControl(fieldName, this.fb.control(''));
      if (value) {
        this.diagnosisIcdForm.patchValue({ [fieldName]: value });
      }
      this.visibleDiagnosisFieldsDialog.push(fieldName);
    }
  }

  onPatientIcdCodesDialogChanged(codes: IcdCode[]) {
    this.selectedPatientIcdCodesDialog = codes;
    // Trigger form validation
    this.diagnosisIcdForm.updateValueAndValidity();
  }

  addICD10CodeToDialog(event: MouseEvent, code: any) {
    event.preventDefault();
    event.stopPropagation();

    const currentDiagnosis = this.diagnosisIcdForm.get('diagnosis')?.value;
    if(!currentDiagnosis) {
      this.diagnosisIcdForm.patchValue({ diagnosis: code.description });
    }

    this.icdCodeSelectorDialog.addChip(code).then(() => {
      this.cd.detectChanges();
    });
  }

  async onCloseDiagnosisIcdDialog(save: boolean) {
    if (save) {
      // The patient the dialog was opened for, even if the page has moved on since.
      const patient = this.diagnosisDialogPatient ?? this.patient;
      const diagnosisData: any = { id: patient.id };

      // Add primary diagnosis
      const primaryDiagnosis = this.diagnosisIcdForm.get('diagnosis')?.value;
      if (primaryDiagnosis) {
        diagnosisData.diagnosis = primaryDiagnosis;
      }

      // Add additional diagnosis fields
      this.visibleDiagnosisFieldsDialog.forEach(fieldName => {
        const value = this.diagnosisIcdForm.get(fieldName)?.value;
        if (value) {
          diagnosisData[fieldName] = value;
        }
      });

      // Add ICD codes
      if (this.selectedPatientIcdCodesDialog.length > 0) {
        diagnosisData.icdCodes = this.selectedPatientIcdCodesDialog;
      }

      try {
        await this.patientService.updatePatient(patient.id, diagnosisData)
          .pipe(takeUntil(this.destroy$))
          .toPromise();

        // Update local patient data
        if (this.patient?.id === patient.id) this.patient = { ...this.patient, ...diagnosisData };
        this.selectedPatientIcdCodes = this.selectedPatientIcdCodesDialog;

        this.showToastMessage('Patient diagnosis and ICD-10 codes updated successfully');
      } catch (error) {
        console.error('Error updating patient diagnosis and ICD codes:', error);
        this.showToastMessage('There was an error updating the patient information. Please try again.');
      }

      // Close with 'save' to indicate successful save
      this.diagnosisIcdDialogRef.close('save');
    } else {
      // User chose to skip - set flag and close with 'skip'
      this.skipDiagnosisCheck = true;
      this.diagnosisIcdDialogRef.close('skip');
    }
  }

  /**
   * Get plain text for letter to colleague (for WhatsApp)
   */
  getColleagueLetterPlainText(): string {
    const letterContent = this.colleagueForm.get('letterToColleague')?.value;
    return letterContent ? this.pdfDownloadService.convertToPlainText(letterContent) : '';
  }

  /**
   * Get plain text for letter to GP (for WhatsApp)
   */
  getGPLetterPlainText(): string {
    const letterContent = this.gpForm.get('letterToGP')?.value;
    return letterContent ? this.pdfDownloadService.convertToPlainText(letterContent) : '';
  }

  /**
   * Get plain text for letter to patient (for WhatsApp)
   */
  getPatientLetterPlainText(): string {
    const letterContent = this.patientForm.get('letterToPatient')?.value;
    return letterContent ? this.pdfDownloadService.convertToPlainText(letterContent) : '';
  }

  /**
   * Get plain text for discharge summary (for WhatsApp)
   */
  getDischargeSummaryPlainText(): string {
    const summaryContent = this.notesForm.get('dischargeSummary')?.value;
    return summaryContent ? this.pdfDownloadService.convertToPlainText(summaryContent) : '';
  }

}
